// src/App.js
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  FiChevronLeft, FiChevronRight, FiLogOut, FiMessageSquare, FiCpu, FiList, FiLayers,
  FiFolderPlus, FiTrash2, FiEdit2, FiCheck, FiX, FiRotateCcw, FiSearch,
} from 'react-icons/fi';
import ChatInterface from './components/ChatInterface';
import RightSidebar from './components/RightSidebar';
import ConversationsSidebar from './components/ConversationsSidebar';
import MemoryPanel from './components/MemoryPanel';
import CustomRulesPanel from './components/CustomRulesPanel';
import ProjectView from './components/ProjectView';
import { PROJECT_UPDATED_EVENT, showProjectConversationsTab } from './components/ProjectPanel';
import ProjectSetupModal from './components/ProjectSetupModal';
import ProjectInvitationModal, { InvitationCard } from './components/ProjectInvitationModal';
import ProjectMembersModal from './components/ProjectMembersModal';
import LeaveProjectModal from './components/LeaveProjectModal';
import LoginPage from './components/LoginPage';
import AuthCallback from './auth/AuthCallback';
import DarkModeToggle from './components/DarkModeToggle';
import { useAuth } from './auth/AuthContext';
import { logout } from './auth/AuthContext';
import { DarkModeProvider } from './contexts/DarkModeContext';
import {
  loadConversation,
  loadSharedConversation,
  importSharedConversation,
  listProjects,
  createProject,
  getProject,
  updateProject,
  deleteProject,
  listDeletedProjects,
  restoreProject,
  permanentlyDeleteProject,
  setConversationModel,
  listProjectMembers,
  listProjectInvitations,
} from './utils/conversationApi';
import {
  getUserIdForSchedule,
  runWeeklyMemoryConsolidateIfDue,
} from './utils/memoryConsolidateSchedule';
import {
  FALLBACK_DEFAULT_MODEL_ID,
  OPUS_5_5_MODEL_ID,
  fetchModelConfig,
  resolveSelectedModel,
  resetSelectedModelForNewConversation,
} from './utils/modelSelection';
import { getApiBaseUrl, authenticatedFetch } from './utils/api';
import { projectFolderName, recycleBinMessage, sharedDeleteMessage } from './utils/projectSharing';
import { cancelHoverPrefetch, prefetchProjectFiles, prefetchProjectFilesOnHover } from './utils/projectFilesCache';
import { focusModeDisplayName } from './utils/focusModes';
import { useLiveRefresh } from './hooks/useLiveRefresh';
import './App.css';

// Cookie utility functions
const setCookie = (name, value, days = 30) => {
  const expires = new Date();
  expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/`;
};

const getCookie = (name) => {
  const nameEQ = name + "=";
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
};

// The app's current canonical path (see computeCanonicalPath below --
// "/", "/<conversation_id>", "/project/<project_id>" or
// "/project/<project_id>/<conversation_id>"), mirrored into sessionStorage
// (THIS TAB SESSION only, not a cookie/localStorage) every time it changes.
// A plain page refresh already restores from window.location.pathname
// directly (see the mount-time restore effect below) and doesn't need this
// at all -- this key exists for the one case a refresh doesn't cover: MSAL's
// acquireTokenRedirect interactive-reauth flow is a genuine cross-page
// navigation through redirectUri (.../auth/callback, see authConfig.js),
// which always lands back on "/" afterward (AuthCallback.js) unless told
// otherwise, losing whatever path the user was actually on. AuthCallback.js
// reads this key as a fallback restore target for exactly that round trip.
const LAST_APP_PATH_STORAGE_KEY = 'kai_last_app_path';

// Same idea as LAST_APP_PATH_STORAGE_KEY, for a project the new-project
// pop-up (ProjectSetupModal) has just created while its files are still
// uploading -- sometimes for minutes, which is exactly the window in which a
// Chrome/Edge-specific MSAL silent-refresh failure (see utils/api.js's
// interactive-reauth fallback) can force a full-page auth round trip. That
// round trip wipes the pop-up with every other in-memory state; this lands
// the user in the project that was created instead of losing track of it.
const PENDING_PROJECT_SETUP_STORAGE_KEY = 'kai_pending_project_setup_id';

// At this width and below the Conversations sidebar is a full-screen overlay
// (App.css's 768px breakpoint), so leaving it open would hide the chat.
const isFullScreenDrawerViewport = () => (
  window.matchMedia ? window.matchMedia('(max-width: 768px)').matches : false
);

const hasValidProfileImg = (img) => {
  if (!img || typeof img !== 'string') return false;
  const trimmed = img.trim();
  return trimmed.length > 0 && trimmed.toUpperCase() !== 'N/A';
};

const getUserInitials = (user) => {
  if (!user) return '?';
  if (user.given_name || user.family_name) {
    const first = (user.given_name || '').trim().charAt(0);
    const last = (user.family_name || '').trim().charAt(0);
    const initials = `${first}${last}`.toUpperCase();
    if (initials) return initials;
  }
  const name = (user.name || '').trim();
  if (!name) return '?';
  // "Sojka, Aleksandra" → AS; "Aleksandra Sojka" → AS
  if (name.includes(',')) {
    const [last, first] = name.split(',').map((p) => p.trim());
    const a = (first || '').charAt(0);
    const b = (last || '').charAt(0);
    return `${a}${b}`.toUpperCase() || '?';
  }
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
};

/** "Hi, First Last" — prefers given/family name; flips "Last, First" displayName */
const getUserGreeting = (user) => {
  if (!user) return 'Hi';
  const given = (user.given_name || '').trim();
  const family = (user.family_name || '').trim();
  if (given || family) {
    return `Hi, ${[given, family].filter(Boolean).join(' ')}`;
  }
  const name = (user.name || '').trim();
  if (!name) return 'Hi';
  if (name.includes(',')) {
    const [last, first] = name.split(',').map((p) => p.trim());
    const flipped = [first, last].filter(Boolean).join(' ');
    return flipped ? `Hi, ${flipped}` : 'Hi';
  }
  return `Hi, ${name}`;
};

// Parses one of this app's own canonical paths -- "/", "/<conversation_id>",
// "/project/<project_id>" or "/project/<project_id>/<conversation_id>" --
// into what it refers to. "auth", "verify-data-access" and "project" are the
// only reserved top-level segments; any other single segment is treated as an
// attempt at a bare conversation id (ids are opaque uuid4s -- see
// conversation_service.py's create_conversation/create_project -- so
// collisions with a reserved word are not a real concern).
const parseAppPath = (pathname) => {
  const segments = (pathname || '').split('/').filter(Boolean);
  if (segments.length === 0) return { type: 'home' };
  // Both of these render their own component instead of the app chrome. Left
  // unreserved, the restore-on-refresh effect below read them as conversation
  // ids and fired a doomed load -- "/verify-data-access" produced a 404 and an
  // alert("Failed to load conversation") over the access check.
  if (segments[0] === 'auth' || segments[0] === 'verify-data-access') {
    return { type: 'reserved' };
  }
  if (segments[0] === 'project' && segments[1]) {
    return { type: 'project', projectId: segments[1], conversationId: segments[2] || null };
  }
  if (segments.length === 1) {
    return { type: 'conversation', conversationId: segments[0] };
  }
  return { type: 'unknown' };
};

// The inverse of parseAppPath -- the canonical URL for the app's CURRENT
// state, so the two stay in lock-step. Returns null when the URL shouldn't
// be touched at all (the separate ?share= shared-conversation view, which
// keeps its own existing query-param-based handling).
const computeCanonicalPath = ({ view, viewingProject, activeConversation, sharedConversation }) => {
  if (sharedConversation) return null;
  if (view === 'project' && viewingProject?.project_id) {
    const pid = viewingProject.project_id;
    if (activeConversation?.conversation_id && activeConversation.project_id === pid) {
      return `/project/${pid}/${activeConversation.conversation_id}`;
    }
    return `/project/${pid}`;
  }
  if (activeConversation?.conversation_id) {
    return `/${activeConversation.conversation_id}`;
  }
  return '/';
};

const App = () => {
  // Authentication context
  const { user, isLoading, accessToken, authNotice, setAuthNotice } = useAuth();
  
  // All hooks must be called at the top level before any early returns
  // Core state
  const [messages, setMessages] = useState([]);
  
  // UI state
  const [rightSidebarOpen, setRightSidebarOpen] = useState(false);
  const [conversationsSidebarOpen, setConversationsSidebarOpen] = useState(false);
  const [memoryPanelOpen, setMemoryPanelOpen] = useState(false);
  const [customRulesPanelOpen, setCustomRulesPanelOpen] = useState(false);
  // Single width shared by all three mutually-exclusive right-hand drawers
  // (Conversations, Memory, Logs) so switching between them never causes a
  // layout shift. Resizing any one drawer updates this value and the new
  // width persists across panel switches.
  const [rightDrawerWidth, setRightDrawerWidth] = useState(360);
  const [activeConversation, setActiveConversation] = useState(null);

  // One-shot: the project the NEXT auto-created conversation should be filed
  // into, set right after the user creates a project (or clicks "new
  // conversation in this project" on an existing one). ChatInterface.js sends
  // this along as new_conversation_project_id on the very first /chat request
  // for that conversation, so the backend files it into the project AT
  // CREATION TIME -- not after the fact via a separate PATCH once the stream
  // finishes, which used to leave a real gap where the first turn ran with no
  // project context at all. Cleared as soon as it's consumed (the conversation
  // auto-creates) or superseded (the user loads a different, existing
  // conversation instead of starting a fresh one -- see handleConversationSelect).
  // See .claude/plan/plan_projects.md.
  //
  // Plain in-memory state, not sessionStorage+reload -- an earlier version
  // reloaded the whole page to get a blank ChatInterface, which was slow and
  // dropped the user out of the project's own look back onto a generic chat
  // screen. handleStartConversationInProject now just sets this directly and
  // bumps chatSessionKey to force ChatInterface to remount in place instead.
  const [pendingProjectForNextConversation, setPendingProjectForNextConversation] = useState(null);
  // Whatever the user had already typed into ProjectView.js's (or the
  // project sidebar's) composer before starting the conversation -- seeded
  // straight into ChatInterface's composer on the remount that follows, and
  // auto-sent once that instance mounts (see its pendingProjectFirstMessage
  // effect).
  const [pendingProjectFirstMessage, setPendingProjectFirstMessage] = useState(null);
  // The Knowledge base subset picked in ProjectPanel.js's Files tab
  // (checkboxes, mirroring its Rules tab) for the conversation about to be
  // created -- read by ChatInterface's sendMessage as
  // new_conversation_knowledge_scope on the very first turn, the same way
  // pendingProjectForNextConversation's project_id is sent as
  // new_conversation_project_id. Lives here (not locally in either
  // component) because ProjectPanel (which writes it) and ChatInterface
  // (which reads it at send time) are SIBLINGS under ProjectView, not
  // ancestor/descendant. See .claude/plan/plan_knowledge_scope.md.
  const [pendingKnowledgeScope, setPendingKnowledgeScope] = useState(null);
  // Bumped by handleStartConversationInProject to force ChatInterface to
  // fully remount (fresh internal state) without reloading the browser page.
  const [chatSessionKey, setChatSessionKey] = useState(0);

  // Which top-level screen occupies the chat column: the normal chat, or a
  // project (ProjectView.js, reached from the "Projects" section of the user
  // dropdown -- see the Focus modes section right below it for the pattern
  // this mirrors).
  //
  // 'project' covers a project's landing page AND every conversation inside
  // it: ProjectView is the shell either way, with the live <ChatInterface>
  // handed to it as children once a conversation is open (see
  // chatBelongsToViewingProject below). Anything with a project_id lands
  // here, so entering a conversation never swaps the project's identity out
  // for a generic chat screen.
  const [view, setView] = useState('chat'); // 'chat' | 'project'
  const [viewingProject, setViewingProject] = useState(null);
  // Captured ONCE, synchronously, at mount -- before the "mirror the
  // canonical path into the URL" effect further below has a chance to run.
  // That effect's very first run (view still 'chat', the initial state
  // above) would otherwise immediately replaceState to "/", overwriting
  // whatever path the user actually loaded before the URL-restore effect
  // gets a chance to read it -- the same race the old sessionStorage-based
  // project restore used to have (and solved the same way: read it into a
  // ref right here, before either effect exists).
  const initialPathRef = useRef(undefined);
  if (initialPathRef.current === undefined) {
    initialPathRef.current = window.location.pathname;
  }
  // True from a load (or reload) on a conversation's or project's own address
  // until the URL-restore effect below has opened it. Until then the chat
  // column shows only a spinner: what it would otherwise show is the plain
  // chat screen's empty composer, which a project conversation then swaps
  // for the project's own screen a second or two later. 'project' or 'chat'
  // says which screen is on its way, so the spinner sits on the same
  // background. The ?conversation=/?share= links aren't restored by that
  // effect (they have their own), so they never set this.
  const [restoringFromUrl, setRestoringFromUrl] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('conversation') || params.get('share')) return null;
    const parsed = parseAppPath(window.location.pathname);
    if (parsed.type === 'project') return 'project';
    if (parsed.type === 'conversation') return 'chat';
    return null;
  });
  const [dropdownProjects, setDropdownProjects] = useState([]);
  // The Projects pop-up's tab ('all' | 'mine' | 'shared') and search box.
  const [projectsFilter, setProjectsFilter] = useState('all');
  const [projectSearch, setProjectSearch] = useState('');
  // Member lists for the Projects pop-up's avatar stacks, keyed by
  // project_id -- fetched lazily (only once the pop-up is actually
  // opened, not on every app load) and cached here for the rest of the
  // session, same per-project listProjectMembers call ProjectPanel.js's own
  // avatar stack already uses. See the fetch effect below.
  // Everyone on the project, pending invitees included (flagged `pending`):
  // the avatars show only people who have joined, the hover text lists both.
  const [dropdownProjectMembers, setDropdownProjectMembers] = useState({});
  const dropdownMembersFetchedRef = useRef(new Set());
  // The members popup (and, from it, the leave dialog) opened straight from a
  // row's avatars in the Projects pop-up, without opening the project -- or
  // from Share's "no members yet" note in a project conversation.
  const [membersModalProject, setMembersModalProject] = useState(null);
  const [leaveModalProject, setLeaveModalProject] = useState(null);
  // The project a project-invite email's link (?project=<id>) pointed at,
  // captured once at load -- the deep-link effect below clears the URL param
  // as soon as it has tried (and, for a not-yet-accepted invite, failed) to
  // open it. Opens the invitation prompt on that project (see the
  // invitations effect below); accepting it then opens the project, as
  // accepting any invitation does (handleInvitationAccepted).
  const inviteLinkProjectIdRef = useRef(new URLSearchParams(window.location.search).get('project'));
  // Project invitations waiting for this user's Accept/Decline, and whether
  // their prompt (ProjectInvitationModal) is showing. The list drives three
  // things: the prompt itself, the Projects icon's badge, and the invitation
  // cards at the top of the Projects menu -- whose "Review" opens the prompt
  // (on that invitation: `invitationFocusId`). The prompt never opens on its
  // own at sign-in; only an invite email's link opens it directly.
  const [projectInvitations, setProjectInvitations] = useState([]);
  const [invitationPromptOpen, setInvitationPromptOpen] = useState(false);
  const [invitationFocusId, setInvitationFocusId] = useState(null);
  // How many invitations the user has already seen in the Projects menu. The
  // badge pulses only while there are more than this; it stays as a plain
  // badge (until the invitation is answered) once they have looked.
  const [acknowledgedInvitationCount, setAcknowledgedInvitationCount] = useState(0);
  // Same capture-into-a-ref-before-any-effect-runs pattern as
  // initialPathRef above, for a project created by the new-project pop-up
  // whose files were still uploading when the tab last unloaded (see
  // PENDING_PROJECT_SETUP_STORAGE_KEY and the restore effect further below).
  const initialProjectSetupIdRef = useRef(undefined);
  if (initialProjectSetupIdRef.current === undefined) {
    try {
      initialProjectSetupIdRef.current = sessionStorage.getItem(PENDING_PROJECT_SETUP_STORAGE_KEY);
    } catch (e) {
      initialProjectSetupIdRef.current = null;
    }
  }
  // Whether the new-project pop-up (ProjectSetupModal) is open. Nothing is
  // created until its Done -- see handleCreateProjectFromSetup.
  const [projectSetupOpen, setProjectSetupOpen] = useState(false);
  // The DOM node behind ProjectView's own breadcrumb-row slot (see
  // headerActionsSlotRef, passed to <ProjectView> further below) -- null
  // until that div's ref callback fires on mount. chatInterface's
  // headerActionsPortalTarget only portals into it once this is set, so on
  // the very first render of a project's chat these controls briefly render
  // inline in ChatInterface's own header (target still null) before jumping
  // up into the breadcrumb one render later -- imperceptible in practice,
  // and never wrong (never null AND embedded without a fallback).
  const [projectHeaderActionsEl, setProjectHeaderActionsEl] = useState(null);
  // Same idea for the slot directly under ProjectView's title, which the
  // model/message-count subtitle portals into (headerSubtitlePortalTarget).
  const [projectHeaderSubtitleEl, setProjectHeaderSubtitleEl] = useState(null);
  // project_id currently mid-delete from the Projects pop-up's own
  // hover-to-delete button (see handleDeleteProjectFromDropdown) -- disables
  // just that row's button and swaps its icon for a spinner, rather than a
  // separate loading flag per row.
  const [deletingProjectId, setDeletingProjectId] = useState(null);
  // The project pending confirmation from that same hover-to-delete button --
  // a styled dialog (see .app-modal-* in App.css) instead of window.confirm,
  // matching ConversationsSidebar's own delete-conversation dialog rather
  // than the browser's bare native prompt.
  const [deleteProjectConfirm, setDeleteProjectConfirm] = useState(null);
  // project_id of the row currently showing an inline rename input in either
  // Projects pop-up (see handleStartRenameProjectFromDropdown) -- no
  // confirmation dialog needed here, unlike delete, since renaming isn't
  // destructive.
  const [renamingProjectId, setRenamingProjectId] = useState(null);
  const [renameProjectDraft, setRenameProjectDraft] = useState('');
  const [renamingProjectBusy, setRenamingProjectBusy] = useState(false);
  // The Projects pop-up's Recycle Bin -- a second view swapped into the
  // SAME popup (see the footer link below the project list) rather than a
  // separate modal, since it's really just "the other list of projects."
  const [recycleBinOpen, setRecycleBinOpen] = useState(false);
  const [deletedProjects, setDeletedProjects] = useState([]);
  const [loadingDeletedProjects, setLoadingDeletedProjects] = useState(false);
  const [restoringProjectId, setRestoringProjectId] = useState(null);
  const [permanentlyDeletingProjectId, setPermanentlyDeletingProjectId] = useState(null);
  // The project pending confirmation for "Delete forever" (see
  // .app-modal-* below, same styled-dialog pattern as deleteProjectConfirm) --
  // permanent, so it gets its own explicit confirm step separate from the
  // Recycle Bin's own move-there confirmation.
  const [permanentDeleteConfirm, setPermanentDeleteConfirm] = useState(null);
  // Seeds from the cookie -- i.e. whatever model the user last used, in this
  // browser, anywhere -- not a hardcoded default. A conversation actually
  // being restored (handleConversationSelect, the ?conversation= / URL-path
  // restores) overrides this right after mount with that conversation's own
  // persisted model_id regardless. The one exception to "last used" carrying
  // over is Opus 5.5, handled separately by the no-active-conversation reset
  // effect below (resetSelectedModelForNewConversation). Validated against
  // /models on mount below regardless.
  const [selectedModel, setSelectedModel] = useState(() => {
    const savedModel = getCookie('selectedModel');
    return savedModel || FALLBACK_DEFAULT_MODEL_ID;
  });
  // Initialize selectedFocusMode from cookie or use default
  const [selectedFocusMode] = useState(() => {
    const savedFocusMode = getCookie('selectedFocusMode');
    return savedFocusMode || 'default';
  });
  const [tokenCount, setTokenCount] = useState(0);
  const [initialContextUsage, setInitialContextUsage] = useState(null);
  const [conversationsRefreshTrigger, setConversationsRefreshTrigger] = useState(0);
  // Bumped each time a reply finishes, so both conversation lists re-read the
  // row it just changed (time, preview, focus mode tag) without a reload.
  const [conversationActivityTrigger, setConversationActivityTrigger] = useState(0);
  const [isResizing, setIsResizing] = useState(null);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [projectsListOpen, setProjectsListOpen] = useState(false);
  const [dropdownFocusModes, setDropdownFocusModes] = useState([]);
  const [dropdownMcpServers, setDropdownMcpServers] = useState({});
  const [dropdownMcpServersLoading, setDropdownMcpServersLoading] = useState(true);
  const [sharedConversation, setSharedConversation] = useState(null);
  const [isImportingShared, setIsImportingShared] = useState(false);
  const [loadingConversationId, setLoadingConversationId] = useState(null);
  const [sharedConversationError, setSharedConversationError] = useState(null);
  const [sharedConversationLoading, setSharedConversationLoading] = useState(false);
  const [showImportSuccessModal, setShowImportSuccessModal] = useState(false);
  // The project the last import went into, for its confirmation ('' if none).
  const [importedIntoProjectName, setImportedIntoProjectName] = useState('');
  const [websitePreview, setWebsitePreview] = useState(null); // { project_id, url, files, refreshKey }
  const [memoryConsolidateToast, setMemoryConsolidateToast] = useState(null);
  // Error-styled counterpart to memoryConsolidateToast, for an action that
  // failed with a message worth actually reading -- e.g. a new project's
  // files that couldn't be uploaded (see handleProjectSetupFinished).
  const [errorToast, setErrorToast] = useState(null);
  const showErrorToast = (message) => {
    setErrorToast(message);
    setTimeout(() => setErrorToast(null), 8000);
  };

  const rightResizeRef = useRef(null);
  const userDropdownRef = useRef(null);

  // Handle routing based on current path
  const currentPath = window.location.pathname;
  
  // Environment-aware paths
  const isProxy = window.location.pathname.includes('/proxy/');
  // Absolute (not './KAI.png') -- a relative path resolves against the
  // CURRENT URL's path, which now that conversations/projects have their
  // own nested paths (/project/<id>/<id>, see computeCanonicalPath above)
  // is no longer always "/". A relative path there resolved to
  // /project/<id>/KAI.png instead, 404ing and leaving just the alt text.
  const logoPath = isProxy ? '/proxy/3000/KAI.png' : '/KAI.png';

  // Reconcile the current selection with backend AVAILABLE_MODELS (e.g. retired Sonnet 4.6).
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const { models, defaultModelId } = await fetchModelConfig();
        if (cancelled) return;

        setSelectedModel((current) => {
          const resolved = resolveSelectedModel(current, models, defaultModelId);
          return resolved === current ? current : resolved;
        });
      } catch (error) {
        console.warn('Could not validate saved model against /models:', error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  // Save selectedModel to cookie whenever it changes -- this is what makes a
  // fresh mount / a genuinely new conversation seed from "whatever model the
  // user last used" instead of a hardcoded default (see the Opus 5.5
  // exception below and the useState initializer above).
  useEffect(() => {
    setCookie('selectedModel', selectedModel);
  }, [selectedModel]);

  // Separately remembers the last NON-Opus model -- what the Opus reset
  // below falls back to. Without tracking this on its own, GPT -> Opus ->
  // refresh would have nothing to fall back to but a hardcoded default: this
  // makes it land back on GPT (whatever was in use right before switching to
  // Opus) instead.
  useEffect(() => {
    if (selectedModel === OPUS_5_5_MODEL_ID) return;
    setCookie('lastNonOpusModel', selectedModel);
  }, [selectedModel]);

  // Persist a model switch onto the active conversation itself -- model_id
  // used to be write-once (set only at creation in create_conversation), so
  // switching models mid-conversation only ever changed local state, never
  // the conversation's own stored model_id. Reopening that conversation
  // (refresh, or picking it again from the sidebar) then went through
  // handleConversationSelect, which resolves selectedModel FROM the
  // conversation's stored model_id -- snapping back to whatever model it was
  // created with instead of the one last picked. Guarded on the two values
  // actually differing: right after handleConversationSelect itself sets
  // selectedModel to match the just-loaded conversation, they're already
  // equal, so this doesn't fire a redundant PUT on every conversation
  // switch -- only for a genuine user-initiated model change.
  useEffect(() => {
    if (!activeConversation?.conversation_id) return;
    if (selectedModel === activeConversation.model_id) return;
    const conversationId = activeConversation.conversation_id;
    setConversationModel(conversationId, selectedModel)
      .then(() => {
        setActiveConversation((current) => (
          current?.conversation_id === conversationId
            ? { ...current, model_id: selectedModel }
            : current
        ));
      })
      .catch((error) => {
        console.warn('Failed to persist model selection to conversation:', error);
      });
  }, [selectedModel, activeConversation]);

  // Resets the model picker away from Opus 5.5 back to the last NON-Opus
  // model used (see lastNonOpusModel cookie above), whenever there's no
  // active conversation -- freshly mounted, or the user just started a new
  // one (handleNewConversation, handleStartConversationInProject, and the
  // other "land on a blank composer" handlers below all get there by nulling
  // activeConversation). Any OTHER last-used model (GPT, Gemini, Sonnet
  // itself) carries over unchanged -- only Opus is expensive/opt-in enough
  // to be worth not defaulting a new conversation into by accident (see
  // resetSelectedModelForNewConversation). Deliberately synchronous (no
  // fetchModelConfig() await, unlike the reconcile-on-mount effect above) so
  // it can't race a conversation-restore effect's own setSelectedModel call:
  // those always set activeConversation non-null in the same pass, at which
  // point this is a no-op.
  useEffect(() => {
    if (activeConversation) return;
    setSelectedModel((current) => (
      resetSelectedModelForNewConversation(current, getCookie('lastNonOpusModel'))
    ));
  }, [activeConversation]);

  // Save selectedFocusMode to cookie whenever it changes
  useEffect(() => {
    setCookie('selectedFocusMode', selectedFocusMode);
    // console.log(`Focus mode selection saved to cookie: ${selectedFocusMode}`);
  }, [selectedFocusMode]);



  // Weekly silent memory consolidation (localStorage-gated per user)
  useEffect(() => {
    if (isLoading || !user || !accessToken) return undefined;
    const userId = getUserIdForSchedule(user);
    if (!userId) return undefined;

    let cancelled = false;
    runWeeklyMemoryConsolidateIfDue(userId).then(({ ran, totalDeleted }) => {
      if (cancelled || !ran || totalDeleted <= 0) return;
      setMemoryConsolidateToast(`Cleaned up ${totalDeleted} duplicate memories`);
      const timer = setTimeout(() => setMemoryConsolidateToast(null), 6000);
      return () => clearTimeout(timer);
    }).catch((err) => {
      console.warn('Weekly memory consolidation failed:', err);
    });

    return () => { cancelled = true; };
  }, [isLoading, user, accessToken]);

  // Handle mouse down for resizing
  const handleMouseDown = (side) => (e) => {
    e.preventDefault();
    setIsResizing(side);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  // Handle mouse move for resizing
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizing) return;

      if (isResizing === 'right') {
        const newWidth = Math.max(250, Math.min(720, window.innerWidth - e.clientX));
        setRightDrawerWidth(newWidth);
      }
    };

    const handleMouseUp = () => {
      setIsResizing(null);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  // Drawer-style panels — Conversations, Memory, Custom Rules, and (admin)
  // Logs — are mutually exclusive. Opening more than one at a time crowds the
  // chat column on desktop and, on mobile, stacks full-screen overlays with
  // identical z-index so only the later one in the DOM is interactable.
  // Opening any panel always closes the other three; closing a panel leaves
  // the others untouched. The one exception is Conversations: the other
  // panels only cover it, so it comes back once they close -- only its own
  // X (or the header button) actually closes it. The Left Sidebar
  // (settings/model picker) is a persistent nav and intentionally not part
  // of this exclusive group.
  const toggleRightSidebar = useCallback(() => {
    setRightSidebarOpen((prev) => {
      const next = !prev;
      if (next) {
        setMemoryPanelOpen(false);
        setCustomRulesPanelOpen(false);
      }
      return next;
    });
  }, []);

  const conversationsSidebarCovered = memoryPanelOpen || customRulesPanelOpen || rightSidebarOpen;
  const conversationsSidebarShown = conversationsSidebarOpen && !conversationsSidebarCovered;

  // While covered by another panel, the header button brings Conversations
  // back to the front rather than closing it unseen.
  const toggleConversationsSidebar = () => {
    if (conversationsSidebarShown) {
      setConversationsSidebarOpen(false);
      return;
    }
    setConversationsSidebarOpen(true);
    setMemoryPanelOpen(false);
    setRightSidebarOpen(false);
    setCustomRulesPanelOpen(false);
  };

  const toggleMemoryPanel = () => {
    setMemoryPanelOpen((prev) => {
      const next = !prev;
      if (next) {
        setRightSidebarOpen(false);
        setCustomRulesPanelOpen(false);
      }
      return next;
    });
  };

  const clearShareFromUrl = () => {
    const params = new URLSearchParams(window.location.search);
    if (params.has('share')) {
      params.delete('share');
      const newSearch = params.toString();
      window.history.replaceState({}, '', window.location.pathname + (newSearch ? `?${newSearch}` : ''));
    }
  };

  const clearConversationFromUrl = () => {
    const params = new URLSearchParams(window.location.search);
    if (params.has('conversation')) {
      params.delete('conversation');
      const newSearch = params.toString();
      window.history.replaceState({}, '', window.location.pathname + (newSearch ? `?${newSearch}` : ''));
    }
  };

  const clearProjectFromUrl = () => {
    const params = new URLSearchParams(window.location.search);
    if (params.has('project')) {
      params.delete('project');
      const newSearch = params.toString();
      window.history.replaceState({}, '', window.location.pathname + (newSearch ? `?${newSearch}` : ''));
    }
  };

  // The full project object behind a conversation's project_id, preferring
  // the already-loaded dropdown list so switching between conversations in
  // the same project costs no extra request. Null for an unfiled
  // conversation, or if the project can't be loaded (a stale id) -- either
  // way the caller falls back to the plain chat screen.
  const resolveProject = async (projectId) => {
    if (!projectId) return null;
    const known = dropdownProjects.find((p) => p.project_id === projectId);
    if (known) return known;
    try {
      return await getProject(projectId);
    } catch (error) {
      console.error('Error loading project for conversation:', error);
      return null;
    }
  };

  const handleConversationSelect = async (conversation) => {
    if (!conversation) return;
    if (loadingConversationId) return;
    // Loading an existing conversation means the "create project, then start
    // a conversation" flow this pending assignment was waiting for isn't
    // happening right now -- drop it rather than let it surface later for an
    // unrelated fresh chat.
    setPendingProjectForNextConversation(null);
    setPendingProjectFirstMessage(null);
    setPendingKnowledgeScope(null);
    clearShareFromUrl();
    clearConversationFromUrl();
    setSharedConversation(null);
    setSharedConversationError(null);
    setLoadingConversationId(conversation.conversation_id);
    try {
      const data = await loadConversation(conversation.conversation_id);
      // A conversation filed under a project opens inside that project's
      // shell (breadcrumb, name, Instructions/Knowledge base/Project conversations), never
      // on the bare chat screen -- whether it was clicked in the project's
      // own History list or in the general Conversations sidebar. Decided
      // here, after the load, rather than optimistically up front, so
      // switching between two project conversations doesn't flash the plain
      // chat screen in between. The project is fetched BEFORE any state
      // below is set, so the conversation and its shell land in one render:
      // awaiting it after setActiveConversation showed the conversation on
      // the plain chat screen for as long as getProject took (on a reload,
      // before the projects list has loaded, a second or two).
      const [{ models, defaultModelId }, project] = await Promise.all([
        fetchModelConfig(),
        resolveProject(data.conversation.project_id),
      ]);
      const modelId = resolveSelectedModel(
        data.conversation.model_id,
        models,
        defaultModelId,
      );

      if (modelId !== selectedModel) {
        setSelectedModel(modelId);
      }

      setActiveConversation(data.conversation);
      setMessages(data.messages || []);

      if (project) {
        setViewingProject(project);
        setView('project');
      } else {
        setView('chat');
      }

      // Restore website preview from last website_preview message
      const lastPreview = (data.messages || [])
        .filter(m => m.additional_data?.data_type === 'website_preview')
        .pop();
      if (lastPreview) {
        setWebsitePreview({
          project_id: lastPreview.additional_data.project_id,
          url: lastPreview.additional_data.url,
          files: lastPreview.additional_data.files,
          refreshKey: Date.now(),
        });
      } else {
        setWebsitePreview(null);
      }

      setTokenCount(0);

      if (data.context_usage && data.context_usage.context_window) {
        setInitialContextUsage(data.context_usage);
      } else {
        setInitialContextUsage(null);
      }

      // The sidebar stays open after picking a regular conversation so the
      // user can keep switching between them; its X is what closes it. A
      // project conversation opens in the project shell, whose own panel
      // takes the sidebar's place, and on a phone the sidebar covers the chat.
      if (project || isFullScreenDrawerViewport()) {
        setConversationsSidebarOpen(false);
      }
    } catch (error) {
      console.error('Error loading conversation:', error);
      alert('Failed to load conversation');
    } finally {
      setLoadingConversationId(null);
    }
  };

  // "New conversation in this project", from ProjectView.js's composer or
  // its History section's "+ New" button. Transitions in place -- no page
  // reload, and the project shell around the chat never unmounts -- so the
  // breadcrumb, project name, and side panel stay exactly where they were
  // and only the middle of the page changes. Bumping chatSessionKey forces
  // ChatInterface to remount from a clean slate, so this also works
  // mid-conversation (clicking "+ New" while already chatting in the
  // project).
  const handleStartConversationInProject = (project, firstMessageText) => {
    // The project's own Instructions/Knowledge base/History panel is what
    // belongs on screen once a project is open -- a drawer left open from
    // whatever the user was doing in plain chat beforehand (the
    // conversations list, in particular) would otherwise keep hiding it
    // behind itself (see anyRightDrawerOpen/hidePanel) until the user
    // happened to close that drawer manually.
    setConversationsSidebarOpen(false);
    setMemoryPanelOpen(false);
    setCustomRulesPanelOpen(false);
    setRightSidebarOpen(false);
    clearShareFromUrl();
    setSharedConversation(null);
    setSharedConversationError(null);
    // A knowledge-base scope picked in ProjectPanel's browse view (see
    // onPendingKnowledgeScopeChange there) is only meaningful for the
    // project it was picked in -- carrying it into a DIFFERENT project's
    // first message would attach file/folder ids that project doesn't own,
    // which validate_knowledge_scope_selection then filters down to an
    // empty 'selected' scope (searches nothing, not "no restriction").
    // Re-entering the SAME project's pending-conversation flow (e.g.
    // clicking "+ New" again before sending) must NOT clear it, so this
    // only fires on an actual project switch.
    const previousProjectId = activeConversation?.project_id || pendingProjectForNextConversation?.project_id || null;
    if (previousProjectId && previousProjectId !== project?.project_id) {
      setPendingKnowledgeScope(null);
    }
    setActiveConversation(null);
    setMessages([]);
    setTokenCount(0);
    setInitialContextUsage(null);
    setWebsitePreview(null);
    setPendingProjectForNextConversation(project);
    setPendingProjectFirstMessage(firstMessageText || null);
    setChatSessionKey((k) => k + 1);
    setViewingProject(project);
    setView('project');
  };

  // "Projects" entry in the user dropdown (and the header's dedicated
  // Projects button, and the chat header's inline project badge) -- opens a
  // project's full-page home.
  //
  // Delegates to handleStartConversationInProject (no first message) unless
  // the caller is already looking at a conversation that belongs to this
  // project -- an active one, or a landing composer already pending for it.
  // Without that guard this would unconditionally reset activeConversation/
  // messages, which would wipe out whatever the user is mid-conversation on
  // every time they merely re-click the project's own entry point (or the
  // inline badge that jumps from the plain chat screen back into the project
  // shell around the SAME conversation). When there's genuinely nothing of
  // this project's already in view, starting a fresh (uncreated) conversation
  // is what makes the landing page's composer the real, fully-featured
  // ChatInterface -- attachments, drag/drop, dictation, ad hoc rules -- for
  // the very first message, not the disabled look-alike ProjectView used to
  // fall back on.
  const handleOpenProjectView = (project) => {
    // Starts the Knowledge base list loading now, not once the panel mounts.
    prefetchProjectFiles(project.project_id);
    setUserDropdownOpen(false);
    setProjectsListOpen(false);
    const conversationAlreadyInThisProject = (
      activeConversation?.project_id === project.project_id
      || pendingProjectForNextConversation?.project_id === project.project_id
    );
    if (!conversationAlreadyInThisProject) {
      handleStartConversationInProject(project);
      return;
    }
    // Same reasoning as handleStartConversationInProject's own drawer-close --
    // this branch reaches the project shell without going through that
    // function, so it needs the same fix.
    setConversationsSidebarOpen(false);
    setMemoryPanelOpen(false);
    setCustomRulesPanelOpen(false);
    setRightSidebarOpen(false);
    setViewingProject(project);
    setView('project');
  };

  // The user accepted a project invitation (ProjectInvitationModal): it joins
  // their "Shared with me" list straight away, and they land inside it --
  // accepting is choosing to start working in it, wherever the prompt was
  // opened from (the Projects menu's card or the invite email's link).
  const handleInvitationAccepted = (project) => {
    setDropdownProjects((prev) => (
      prev.some((p) => p.project_id === project.project_id) ? prev : [...prev, project]
    ));
    // Conversations brought back from their saved folder just left it (and the
    // folder may be gone): re-read the regular conversations list now.
    if (project.restored_conversation_count > 0) {
      setConversationsRefreshTrigger((prev) => prev + 1);
    }
    if (inviteLinkProjectIdRef.current === project.project_id) {
      inviteLinkProjectIdRef.current = null;
    }
    handleOpenProjectView(project);
  };

  // An invitation was accepted, declined, or turned out to be gone: drop it
  // from the list and close the prompt. Any others stay as cards in the
  // Projects menu -- each is reviewed on purpose, not rolled into one after
  // another.
  const handleInvitationAnswered = (projectId) => {
    setProjectInvitations((prev) => prev.filter((i) => i.project_id !== projectId));
    setInvitationPromptOpen(false);
    setInvitationFocusId(null);
  };

  const openInvitationPrompt = (projectId = null) => {
    setProjectsListOpen(false);
    setInvitationFocusId(projectId);
    setInvitationPromptOpen(true);
  };

  const closeInvitationPrompt = () => {
    setInvitationPromptOpen(false);
    setInvitationFocusId(null);
  };

  const handleProjectDeleted = (projectId) => {
    setDropdownProjects((prev) => prev.filter((p) => p.project_id !== projectId));
    // Also what runs when someone LEAVES a shared project: the conversations
    // they chose to save now sit in a "Project-<name>" folder in their regular
    // list, so the sidebar re-reads it straight away rather than on its next poll.
    setConversationsRefreshTrigger((prev) => prev + 1);
    // The project moved to the Recycle Bin (or was purged outright) is now
    // unreachable either way -- if the conversation currently loaded in chat
    // belonged to it, drop back to plain chat rather than leave it showing a
    // conversation whose project no longer resolves.
    if (activeConversation?.project_id === projectId) {
      setActiveConversation(null);
      setMessages([]);
    }
    // A brand-new project's own landing composer (no conversation created
    // yet -- see handleStartConversationInProject) is tracked here, not on
    // activeConversation, so deleting it needs the same guard as that branch
    // above or ChatInterface keeps rendering "Start a conversation in
    // <deleted project>" (getActiveProject() falls back to this whenever
    // there's no activeConversation) even after view/viewingProject below
    // have already moved off it.
    if (pendingProjectForNextConversation?.project_id === projectId) {
      setPendingProjectForNextConversation(null);
      setPendingProjectFirstMessage(null);
    }
    // Guarded on the deleted project actually being the one on screen: this
    // is also reachable from the Projects pop-up's own hover-to-delete
    // button (see handleDeleteProjectFromDropdown), which can delete a
    // project other than whichever one -- if any -- is currently being
    // viewed, and that unrelated view shouldn't be kicked back to plain chat
    // just because a different project's row was deleted.
    if (viewingProject?.project_id === projectId) {
      setViewingProject(null);
      setView('chat');
    }
  };
  // For the background projects refresh, which finishes after later renders.
  const projectDeletedRef = useRef(null);
  projectDeletedRef.current = handleProjectDeleted;

  // Hover-to-delete button next to a project's name in the Projects pop-up
  // (see .projects-dropdown-item-delete in App.css) -- a quick way to remove
  // a project without first opening it to reach ProjectView's own "..."
  // menu. Opens the styled confirmation below rather than deleting
  // immediately; the actual delete happens in
  // confirmDeleteProjectFromDropdown once the user confirms there.
  const handleDeleteProjectFromDropdown = (e, project) => {
    e.stopPropagation();
    setDeleteProjectConfirm(project);
  };

  const confirmDeleteProjectFromDropdown = async () => {
    const project = deleteProjectConfirm;
    if (!project) return;
    setDeletingProjectId(project.project_id);
    try {
      await deleteProject(project.project_id, { leave: project.shared_with > 0 });
      // Only after the server has actually confirmed the delete -- no
      // optimistic removal from either dropdown's list before that.
      handleProjectDeleted(project.project_id);
      setDeleteProjectConfirm(null);
    } catch (error) {
      if (error.status === 409) {
        // Someone joined or left since this dialog was shown: reload the
        // project so the still-open dialog says what Delete will really do.
        try {
          const fresh = await getProject(project.project_id);
          setDropdownProjects((prev) => prev.map((p) => (p.project_id === fresh.project_id ? fresh : p)));
          setDeleteProjectConfirm(fresh);
        } catch {
          setDeleteProjectConfirm(null);
        }
      }
      alert(error.message || 'Failed to delete project');
    } finally {
      setDeletingProjectId(null);
    }
  };

  // Hover-to-rename button next to a project's name in the Projects
  // dropdown, right alongside the existing hover-to-delete one -- turns the
  // row's own label into an editable input in place, rather than opening
  // ProjectView first just to reach its "..." menu.
  const handleStartRenameProjectFromDropdown = (e, project) => {
    e.stopPropagation();
    setRenamingProjectId(project.project_id);
    setRenameProjectDraft(project.name);
  };

  const handleCancelRenameProjectFromDropdown = () => {
    setRenamingProjectId(null);
    setRenameProjectDraft('');
  };

  const handleCommitRenameProjectFromDropdown = async (project) => {
    const trimmed = renameProjectDraft.trim();
    if (!trimmed || trimmed === project.name) {
      handleCancelRenameProjectFromDropdown();
      return;
    }
    setRenamingProjectBusy(true);
    try {
      const updated = await updateProject(project.project_id, { name: trimmed });
      // Same cross-sibling pattern ProjectPanel/ProjectView/ProjectSetupModal
      // all use for their own renames -- the effect above (onProjectUpdated)
      // is what actually refreshes dropdownProjects/viewingProject from this.
      window.dispatchEvent(new CustomEvent(PROJECT_UPDATED_EVENT, { detail: updated }));
      handleCancelRenameProjectFromDropdown();
    } catch (error) {
      alert(error.message || 'Failed to rename project');
    } finally {
      setRenamingProjectBusy(false);
    }
  };

  // A conversation was deleted from the project panel's own list. If it's the
  // one the chat pane is showing, that pane is now displaying a conversation
  // that no longer exists -- drop back to the project's landing page rather
  // than leave its messages on screen. Remounting via chatSessionKey clears
  // ChatInterface's internal state too (a half-streamed reply, a draft), which
  // clearing App's own state alone wouldn't.
  const handleProjectConversationDeleted = (conversationId) => {
    refreshConversations();
    if (activeConversation?.conversation_id !== conversationId) return;
    setActiveConversation(null);
    setMessages([]);
    setTokenCount(0);
    setInitialContextUsage(null);
    setWebsitePreview(null);
    setChatSessionKey((k) => k + 1);
  };

  // The Projects pop-up's new-project button (and "Create your first project"): opens the
  // new-project pop-up. Nothing is created until its Done.
  const openNewProjectSetup = () => {
    setProjectsListOpen(false);
    setProjectSetupOpen(true);
  };

  // The new-project pop-up's Done: creates the project with its name and
  // instructions in one request. Left un-caught so the pop-up shows the
  // error by the field it's about and stays open with everything entered.
  const handleCreateProjectFromSetup = async (name, instructions) => {
    const project = await createProject(name, { instructions });
    // Its files upload next, with the pop-up still open -- see
    // PENDING_PROJECT_SETUP_STORAGE_KEY.
    try {
      sessionStorage.setItem(PENDING_PROJECT_SETUP_STORAGE_KEY, project.project_id);
    } catch (e) {
      // Storage disabled -- an interrupted upload just won't land the user
      // back in the project, which they can still open from the list.
    }
    return project;
  };

  // The new project and its files are in (indexing carries on server-side):
  // straight to its page. `failedFileNames`: files that never reached the
  // server, so the Knowledge base tab won't list them.
  const handleProjectSetupFinished = (project, failedFileNames) => {
    try {
      sessionStorage.removeItem(PENDING_PROJECT_SETUP_STORAGE_KEY);
    } catch (e) {
      // Storage disabled -- nothing was saved to clear.
    }
    setProjectSetupOpen(false);
    setDropdownProjects((prev) => (
      prev.some((p) => p.project_id === project.project_id) ? prev : [...prev, project]
    ));
    handleOpenProjectView(project);
    if (failedFileNames.length > 0) {
      const names = failedFileNames.length === 1
        ? `"${failedFileNames[0]}"`
        : `${failedFileNames.length} files`;
      showErrorToast(
        `"${project.name}" was created, but ${names} couldn't be uploaded. `
        + "Add them again from the project's Knowledge base."
      );
    }
  };

  // Fires once, from ChatInterface's conversation_created stream handler, the
  // first time a brand-new conversation auto-creates after pendingProjectFor
  // NextConversation was set. The conversation is already filed into the
  // project server-side by this point (sent as new_conversation_project_id on
  // the very request that created it -- see ChatInterface.js's sendMessage),
  // so there is nothing to confirm here; this just refreshes the sidebar so
  // the project's conversation count updates without waiting on its next poll.
  const handleProjectConversationFiled = () => {
    setPendingProjectForNextConversation(null);
    setPendingProjectFirstMessage(null);
    setPendingKnowledgeScope(null);
    refreshConversations();
  };

  // Share's "Add to a project" just moved the open conversation into
  // `project` (ChatInterface has already updated it): it carries on inside
  // the project's shell, with the panel on the project's Conversations,
  // where it's now listed. The same drawer-closing as opening any project.
  const handleConversationAddedToProject = (project) => {
    showProjectConversationsTab(project.project_id);
    setConversationsSidebarOpen(false);
    setMemoryPanelOpen(false);
    setCustomRulesPanelOpen(false);
    setRightSidebarOpen(false);
    setViewingProject(project);
    setView('project');
    refreshConversations();
  };

  // The Conversations sidebar's "New Conversation" button: a blank, regular
  // conversation in place, without the page reload it used to do (which
  // also closed the sidebar, since that starts out closed). Like landing on
  // "/" any other way, nothing is created until the first message is sent;
  // the URL-sync effect moves the address bar to "/" once
  // activeConversation is cleared.
  const handleNewConversation = () => {
    clearShareFromUrl();
    clearConversationFromUrl();
    setSharedConversation(null);
    setSharedConversationError(null);
    // A new conversation from the Conversations sidebar isn't filed under
    // anything, so it belongs on the general chat screen, not behind
    // whatever project shell happened to be open.
    setView('chat');
    setViewingProject(null);
    setActiveConversation(null);
    setMessages([]);
    setTokenCount(0);
    setInitialContextUsage(null);
    setWebsitePreview(null);
    // Same reason as handleConversationsButtonClick: left set, these would
    // make ChatInterface render the project's empty state and file the
    // first message under that project.
    setPendingProjectForNextConversation(null);
    setPendingProjectFirstMessage(null);
    setPendingKnowledgeScope(null);
    // Fresh ChatInterface state too (a draft, a half-streamed reply), which
    // the reload used to give for free.
    setChatSessionKey((k) => k + 1);
    // Same as handleConversationSelect: the sidebar stays open except on a
    // phone, where it would cover the new conversation.
    if (isFullScreenDrawerViewport()) {
      setConversationsSidebarOpen(false);
    }
  };

  // The header's "Conversations" icon, clicked while viewing a project --
  // rather than just opening the sidebar on top of whatever project chat is
  // showing, this lands the user on a blank, regular (non-project)
  // conversation first, same reset handleNewConversation does for an
  // existing conversation. No conversation is created here though -- like
  // landing on the plain chat screen any other way, one is only created
  // lazily once a first message is actually sent.
  const handleConversationsButtonClick = () => {
    if (view === 'project') {
      clearShareFromUrl();
      setSharedConversation(null);
      setSharedConversationError(null);
      setView('chat');
      setViewingProject(null);
      setActiveConversation(null);
      setMessages([]);
      setTokenCount(0);
      setInitialContextUsage(null);
      setWebsitePreview(null);
      // Without these, ChatInterface's own getActiveProject() falls back to
      // pendingProjectForNextConversation (still set to the project just
      // left, since activeConversation is null) and renders ITS OWN
      // project empty-state -- rocket icon, "Start a conversation in
      // <project>", the knowledge-base-scoped composer -- even once
      // ProjectView itself has correctly unmounted above.
      setPendingProjectForNextConversation(null);
      setPendingProjectFirstMessage(null);
      setPendingKnowledgeScope(null);
      setConversationsSidebarOpen(true);
      setMemoryPanelOpen(false);
      setRightSidebarOpen(false);
      setCustomRulesPanelOpen(false);
      return;
    }
    toggleConversationsSidebar();
  };

  const refreshConversations = () => {
    setConversationsRefreshTrigger(prev => prev + 1);
  };

  const handleMessageSent = () => {
    // If no active conversation, refresh the list to show the auto-created one
    if (!activeConversation) {
      setTimeout(refreshConversations, 1000); // Small delay to ensure backend has processed
    }
    setConversationActivityTrigger((prev) => prev + 1);
  };

  // Called by ChatInterface after POST /conversations/{id}/handover/continue
  // succeeds. The backend has already seeded the new conversation; we load
  // it just like clicking it in the sidebar.
  const handleConversationContinued = async (newConversation) => {
    if (!newConversation) return;
    refreshConversations();
    try {
      await handleConversationSelect(newConversation);
    } catch (err) {
      console.error('Failed to switch to continued conversation:', err);
    }
  };

  // Open a specific conversation by id (used by handover seed banner to
  // jump back to the source conversation).
  const openConversationById = (conversationId) => {
    if (!conversationId) return;
    handleConversationSelect({ conversation_id: conversationId });
  };

  const toggleUserDropdown = () => {
    setProjectsListOpen(false);
    setUserDropdownOpen(!userDropdownOpen);
  };

  // Swaps the Projects pop-up's own list view for its Recycle Bin view --
  // fetched fresh each time it's opened rather than kept warm, since it's
  // rarely visited and can go stale (another tab restoring/purging something).
  const openRecycleBin = async () => {
    setRecycleBinOpen(true);
    setLoadingDeletedProjects(true);
    try {
      const items = await listDeletedProjects();
      setDeletedProjects(items);
    } catch (error) {
      console.error('Error loading Recycle Bin:', error);
    } finally {
      setLoadingDeletedProjects(false);
    }
  };

  const handleRestoreProject = async (project) => {
    setRestoringProjectId(project.project_id);
    try {
      const restored = await restoreProject(project.project_id);
      setDeletedProjects((prev) => prev.filter((p) => p.project_id !== project.project_id));
      setDropdownProjects((prev) => [...prev, restored]);
      // Its conversations just left their "Project-<name>" folder for the project.
      setConversationsRefreshTrigger((prev) => prev + 1);
    } catch (error) {
      alert(error.message || 'Failed to restore project');
    } finally {
      setRestoringProjectId(null);
    }
  };

  const confirmPermanentDelete = async () => {
    const project = permanentDeleteConfirm;
    if (!project) return;
    setPermanentlyDeletingProjectId(project.project_id);
    try {
      await permanentlyDeleteProject(project.project_id);
      setDeletedProjects((prev) => prev.filter((p) => p.project_id !== project.project_id));
      setPermanentDeleteConfirm(null);
    } catch (error) {
      alert(error.message || 'Failed to permanently delete project');
    } finally {
      setPermanentlyDeletingProjectId(null);
    }
  };

  // The Projects pop-up: opened by the top-bar's Projects button and by
  // ProjectView's own "Projects" breadcrumb link alike. Always reopens on the
  // project list, never left on the Recycle Bin from a previous visit.
  const openProjectsList = () => {
    setUserDropdownOpen(false);
    setProjectsListOpen(true);
    setRecycleBinOpen(false);
    setProjectSearch('');
  };

  const handleLogout = async () => {
    setUserDropdownOpen(false);
    await logout();
  };

  const handleDropdownFocusModeSelect = (modeId) => {
    // Inside a project too: the reload restores whatever the address bar
    // says (see initialPathRef), so the user stays on the project's page or
    // in the project conversation, now in the new mode.
    if (modeId === selectedFocusMode) {
      setUserDropdownOpen(false);
      return;
    }
    setCookie('selectedFocusMode', modeId);
    window.location.reload();
  };

  const handleDropdownOpenMemory = () => {
    setUserDropdownOpen(false);
    setRightSidebarOpen(false);
    setCustomRulesPanelOpen(false);
    setMemoryPanelOpen(true);
  };

  const handleDropdownOpenCustomRules = () => {
    setUserDropdownOpen(false);
    setRightSidebarOpen(false);
    setMemoryPanelOpen(false);
    setCustomRulesPanelOpen(true);
  };

  // Fetch focus modes for the user dropdown (permission-filtered)
  useEffect(() => {
    if (!user || !accessToken) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await authenticatedFetch(`${getApiBaseUrl()}/focus-modes`);
        if (!response.ok) {
          throw new Error(`Focus modes request failed (status ${response.status})`);
        }
        const data = await response.json();
        if (!cancelled) {
          setDropdownFocusModes(data.focus_modes || []);
        }
      } catch (err) {
        console.error('Failed to load focus modes for user menu:', err);
        if (!cancelled) {
          setDropdownFocusModes([
            { id: 'default', name: 'General', description: 'All available tools and data sources' },
          ]);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [user, accessToken]);

  // Fetch MCP servers for the user dropdown's "MCP servers" section
  // (formerly the left configuration sidebar's own list, now removed) --
  // same /mcp/servers + /mcp/servers/health calls, same 5-minute
  // localStorage cache key, and the same client-side filtering down to the
  // active focus mode's allowed_servers (see visibleDropdownMcpServers
  // below), so the servers shown here match exactly what that sidebar used
  // to show, just in a new place.
  useEffect(() => {
    const HEALTH_CACHE_KEY = 'mcp_servers_health';
    const CACHE_TTL = 5 * 60 * 1000;
    let cancelled = false;

    const getCachedHealth = () => {
      try {
        const cached = localStorage.getItem(HEALTH_CACHE_KEY);
        if (cached) {
          const { data, timestamp } = JSON.parse(cached);
          if (Date.now() - timestamp < CACHE_TTL) return data;
        }
      } catch (e) {
        console.error('Error reading MCP servers cache:', e);
      }
      return null;
    };
    const setCachedHealth = (data) => {
      try {
        localStorage.setItem(HEALTH_CACHE_KEY, JSON.stringify({ data, timestamp: Date.now() }));
      } catch (e) {
        console.error('Error setting MCP servers cache:', e);
      }
    };

    (async () => {
      const cachedHealth = getCachedHealth();
      if (cachedHealth) {
        setDropdownMcpServers(cachedHealth);
        setDropdownMcpServersLoading(false);
        return;
      }
      try {
        const apiBase = getApiBaseUrl();
        const response = await fetch(`${apiBase}/mcp/servers`);
        if (!response.ok) throw new Error(`Server returned ${response.status}`);
        const data = await response.json();
        if (cancelled) return;
        setDropdownMcpServers(data);
        setDropdownMcpServersLoading(false);
        try {
          const healthResponse = await fetch(`${apiBase}/mcp/servers/health`);
          if (healthResponse.ok) {
            const healthData = await healthResponse.json();
            if (!cancelled) {
              setDropdownMcpServers(healthData);
              setCachedHealth(healthData);
            }
          }
        } catch (healthError) {
          console.error('Error checking MCP server health:', healthError);
        }
      } catch (err) {
        console.error('Failed to load MCP servers for user menu:', err);
        if (!cancelled) {
          setDropdownMcpServers({});
          setDropdownMcpServersLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // The active focus mode's name for the project page's title row, or null in
  // General Focus.
  const activeFocusModeName = useMemo(
    () => focusModeDisplayName(dropdownFocusModes, selectedFocusMode),
    [dropdownFocusModes, selectedFocusMode],
  );

  // Same filtering LeftSidebar used to apply: an empty allowed_servers list
  // on the active focus mode means "no restriction" (show every server);
  // otherwise only the servers that mode names are shown.
  const visibleDropdownMcpServers = useMemo(() => {
    const activeMode = dropdownFocusModes.find((mode) => mode.id === selectedFocusMode);
    const allowedServers = activeMode?.allowed_servers || [];
    if (allowedServers.length === 0) return dropdownMcpServers;
    return Object.fromEntries(
      Object.entries(dropdownMcpServers).filter(([serverName]) => allowedServers.includes(serverName))
    );
  }, [dropdownMcpServers, dropdownFocusModes, selectedFocusMode]);

  // Fetch the user's projects for the Projects pop-up.
  useEffect(() => {
    if (!user || !accessToken) return undefined;
    let cancelled = false;
    listProjects()
      .then((list) => {
        if (!cancelled) setDropdownProjects(list);
      })
      .catch((err) => console.error('Failed to load projects for user menu:', err));
    return () => { cancelled = true; };
  }, [user, accessToken]);

  // Project invitations awaiting this user's answer. Loaded at sign-in, where
  // they only light up the Projects icon's pulsing badge -- the prompt does not
  // open by itself. The one exception is arriving through a project-invite
  // email's link (?project=<id>): that is an explicit "show me", so it opens
  // the prompt on that invitation, once. (`user`/`accessToken` change
  // reference more than once while login resolves, hence the ref guard.)
  // Kept current afterwards by refreshProjectsAndInvites below.
  const inviteLinkPromptShownRef = useRef(false);
  useEffect(() => {
    if (!user || !accessToken) return undefined;
    let cancelled = false;
    listProjectInvitations()
      .then((list) => {
        if (cancelled) return;
        setProjectInvitations(list);
        const linkedId = inviteLinkProjectIdRef.current;
        if (linkedId && !inviteLinkPromptShownRef.current && list.some((i) => i.project_id === linkedId)) {
          inviteLinkPromptShownRef.current = true;
          setInvitationFocusId(linkedId);
          setInvitationPromptOpen(true);
        }
      })
      .catch((err) => console.error('Failed to load project invitations:', err));
    return () => { cancelled = true; };
  }, [user, accessToken]);

  // Re-reads the invitations and the projects list, so what other people do
  // shows up without a reload: an invitation sent to you, people joining or
  // leaving a project (each row says who it's shared with), a project you
  // were removed from or whose owner deleted it. Runs each time the Projects
  // menu opens, and every minute while the tab is visible (useLiveRefresh).
  // Best-effort: a failure leaves both lists as they were. A list that changed
  // here while a reload was on its way (an invitation answered, a project
  // created or renamed) keeps that change; the reload's copy may predate it.
  const projectInvitationsSeqRef = useRef(0);
  useEffect(() => { projectInvitationsSeqRef.current += 1; }, [projectInvitations]);
  const dropdownProjectsSeqRef = useRef(0);
  useEffect(() => { dropdownProjectsSeqRef.current += 1; }, [dropdownProjects]);
  const projectsRefreshRef = useRef(null);
  projectsRefreshRef.current = () => {
    const invitationsSeq = projectInvitationsSeqRef.current;
    listProjectInvitations()
      .then((list) => {
        if (projectInvitationsSeqRef.current === invitationsSeq) setProjectInvitations(list);
      })
      .catch(() => {});
    const projectsSeq = dropdownProjectsSeqRef.current;
    const before = dropdownProjects;
    listProjects()
      .then((list) => {
        if (dropdownProjectsSeqRef.current !== projectsSeq) return;
        setDropdownProjects(list);
        // Gone since: removed from it, or its owner deleted it. If it's the
        // one on screen (or its conversation is), leave it the same way as
        // deleting it here would. The latest handler, not this render's.
        before
          .filter((p) => !list.some((q) => q.project_id === p.project_id))
          .forEach((p) => projectDeletedRef.current?.(p.project_id));
      })
      .catch(() => {});
  };

  useEffect(() => {
    if (!projectsListOpen || !user || !accessToken) return;
    projectsRefreshRef.current();
  }, [projectsListOpen, user, accessToken]);

  useLiveRefresh(() => projectsRefreshRef.current(), {
    intervalMs: 60000,
    enabled: Boolean(user && accessToken),
  });

  // Opening the Projects menu counts as having seen the invitations in it (and
  // follows the list while the menu stays open, e.g. after the refresh above),
  // which is what stops the badge pulsing.
  useEffect(() => {
    if (projectsListOpen || invitationPromptOpen) {
      setAcknowledgedInvitationCount(projectInvitations.length);
    }
  }, [projectsListOpen, invitationPromptOpen, projectInvitations.length]);

  // Lazily fetch each dropdown project's member list (for the avatar stack
  // next to its row) the moment the pop-up is actually opened, not
  // up front for every project on app load -- there's no cap on dropdownProjects.length
  // today (see MAX_PROJECTS_PER_USER), so firing this for every project
  // unconditionally could mean dozens of KPF-directory photo lookups nobody
  // asked to see yet. dropdownMembersFetchedRef makes each project_id a
  // fetch-once, so reopening the pop-up later doesn't repeat the calls.
  useEffect(() => {
    if (!projectsListOpen) return;
    // Also re-fetched when the project's own counts say the cached list is out
    // of date (someone was invited, joined, left or was removed since).
    const isStale = (p) => {
      const cached = dropdownProjectMembers[p.project_id];
      if (!cached) return false;
      const others = (p.shared_with || 0) + (p.pending_invites || 0);
      // A never-shared project lists nobody, or just its owner.
      if (others === 0 && !p.ownerless) return cached.length > 1;
      return cached.length !== others + (p.ownerless ? 0 : 1);
    };
    const toFetch = dropdownProjects.filter(
      (p) => !dropdownMembersFetchedRef.current.has(p.project_id) || isStale(p)
    );
    if (toFetch.length === 0) return;
    toFetch.forEach((p) => dropdownMembersFetchedRef.current.add(p.project_id));
    toFetch.forEach((p) => {
      listProjectMembers(p.project_id)
        .then((list) => {
          setDropdownProjectMembers((prev) => ({ ...prev, [p.project_id]: list }));
        })
        .catch(() => {
          // Best-effort, decorative -- a failed lookup just means that row's
          // avatar stack stays hidden; unmark it so the next pop-up open
          // retries instead of giving up on it forever.
          dropdownMembersFetchedRef.current.delete(p.project_id);
        });
    });
    // dropdownProjectMembers is read for the staleness check only; re-running
    // on its own changes would refetch every row the moment one lands.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectsListOpen, dropdownProjects]);

  // Keep every copy of a project in sync the moment its rules or name are
  // saved anywhere (ProjectPanel.js / ProjectView.js both dispatch this).
  // pendingProjectForNextConversation matters as much as the rest: it's what
  // ChatInterface reads default_rule_ids from before the conversation exists,
  // so without it, attaching an instruction on the landing page wouldn't show
  // up on the first turn.
  useEffect(() => {
    const onProjectUpdated = (e) => {
      const updated = e.detail;
      if (!updated?.project_id) return;
      setDropdownProjects((prev) => prev.map((p) => (p.project_id === updated.project_id ? updated : p)));
      setViewingProject((prev) => (prev?.project_id === updated.project_id ? updated : prev));
      setPendingProjectForNextConversation(
        (prev) => (prev?.project_id === updated.project_id ? updated : prev)
      );
    };
    window.addEventListener(PROJECT_UPDATED_EVENT, onProjectUpdated);
    return () => window.removeEventListener(PROJECT_UPDATED_EVENT, onProjectUpdated);
  }, []);

  // Close the user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target)) {
        setUserDropdownOpen(false);
      }
    };

    if (userDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [userDropdownOpen]);

  // Escape closes the Projects pop-up -- unless a delete confirmation is open
  // over it, or a rename is in progress: Escape there means "cancel that".
  // With something in the search box, the first Escape clears it.
  useEffect(() => {
    if (!projectsListOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key !== 'Escape') return;
      if (deleteProjectConfirm || permanentDeleteConfirm || renamingProjectId) return;
      if (projectSearch && !recycleBinOpen) {
        setProjectSearch('');
        return;
      }
      setProjectsListOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [projectsListOpen, deleteProjectConfirm, permanentDeleteConfirm, renamingProjectId, projectSearch, recycleBinOpen]);

  // Register global functions for sidebar components
  useEffect(() => {
    window.toggleRightSidebar = toggleRightSidebar;

    return () => {
      delete window.toggleRightSidebar;
    };
  }, [toggleRightSidebar]);

  // Share ID from URL so effect re-runs when user navigates to a share link
  const shareIdFromUrl = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('share') : null;

  // Conversation ID from URL so the agent can emit links like
  // [our earlier chat](?conversation=<id>) that open the linked conversation
  // when the user clicks them. Mirrors the `?share=` plumbing above.
  const conversationIdFromUrl = typeof window !== 'undefined'
    ? new URLSearchParams(window.location.search).get('conversation')
    : null;

  useEffect(() => {
    const cid = conversationIdFromUrl;
    if (!cid || !user || !accessToken) return;
    if (activeConversation?.conversation_id === cid) {
      clearConversationFromUrl();
      return;
    }
    (async () => {
      try {
        const data = await loadConversation(cid);
        // Resolve and apply the conversation's own model BEFORE activating it
        // (matches handleConversationSelect) -- setting activeConversation
        // first would let the model-persist effect see the new conversation
        // paired with the OLD selectedModel for a render and PUT that stale
        // value over the conversation's real model_id. Its project too, for
        // the same reason as there: so a project conversation doesn't show on
        // the plain chat screen while getProject is still out.
        const [{ models, defaultModelId }, project] = await Promise.all([
          fetchModelConfig(),
          resolveProject(data.conversation?.project_id),
        ]);
        const modelId = resolveSelectedModel(
          data.conversation?.model_id,
          models,
          defaultModelId,
        );
        if (modelId !== selectedModel) {
          setSelectedModel(modelId);
        }
        setActiveConversation(data.conversation);
        setMessages(data.messages || []);
        setInitialContextUsage(data.context_usage || null);
        // Same rule as handleConversationSelect: a linked conversation that
        // belongs to a project opens inside that project's shell.
        if (project) {
          setViewingProject(project);
          setView('project');
        }
      } catch (err) {
        console.error('Failed to open linked conversation:', err);
      } finally {
        clearConversationFromUrl();
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, accessToken, conversationIdFromUrl]);

  // Project id from URL -- what a project-invite email's link uses
  // (?project=<id>, see share_service.send_project_invite_email). Mirrors
  // the `?conversation=` plumbing above. Someone who has ALREADY accepted
  // lands straight in the project. Someone whose invite is still pending gets
  // a 404 here (an invite grants no access), which is swallowed below -- the
  // ProjectInvitationModal prompt is what they act on, and accepting it
  // opens the project (handleInvitationAccepted).
  const projectIdFromUrl = typeof window !== 'undefined'
    ? new URLSearchParams(window.location.search).get('project')
    : null;

  useEffect(() => {
    const pid = projectIdFromUrl;
    if (!pid || !user || !accessToken) return;
    if (viewingProject?.project_id === pid) {
      clearProjectFromUrl();
      return;
    }
    (async () => {
      try {
        const project = await getProject(pid);
        handleOpenProjectView(project);
      } catch (err) {
        console.error('Failed to open linked project:', err);
      } finally {
        clearProjectFromUrl();
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, accessToken, projectIdFromUrl]);

  // Flips true exactly once, right after the URL-restore attempt below runs
  // (whether or not it actually found a conversation/project to restore) --
  // gates the URL-sync effect further below so ITS very first run (before
  // login even resolves, view/viewingProject/activeConversation all still
  // at their initial empty values) doesn't immediately replaceState to "/",
  // overwriting the real path in initialPathRef before this effect ever
  // gets to read it. A garbage/expired id left in the URL still gets a
  // chance to self-heal back to "/" afterward, since this flips regardless
  // of whether restoration succeeded.
  const [urlRestoreAttempted, setUrlRestoreAttempted] = useState(false);

  // Restores the conversation/project a URL like /project/<pid>/<cid>
  // pointed at, across a plain page refresh (or the very first load of a
  // pasted/bookmarked link) -- no ?conversation=/?share= link involved,
  // since those two effects above already resolve their OWN target and take
  // priority, being a more specific signal of where the user wants to land.
  // Reads the path captured into initialPathRef at mount, NOT
  // window.location.pathname directly -- by the time `user`/`accessToken`
  // resolve, the URL-sync effect below may already have rewritten the
  // actual address bar at least once (see that ref's own comment for the
  // full race).
  //
  // Deliberately no cleanup/cancellation flag here (an earlier version of
  // this same restore-on-refresh idea had one, `let cancelled = false; ...;
  // return () => { cancelled = true; }`, the usual pattern for an async
  // effect). That was the actual bug: `user`/`accessToken` from useAuth()
  // change reference more than once while login is still resolving,
  // re-running this effect and firing that cleanup on the PREVIOUS run --
  // which set ITS closure's `cancelled` to true before ITS OWN
  // resolveProject()/handleConversationSelect() call had even resolved,
  // silently discarding a perfectly good result the instant it came back.
  // Nulling out initialPathRef the moment an attempt starts is what
  // actually prevents duplicate/repeat attempts (a re-run after that finds
  // the ref already empty and bails immediately) -- a stale-closure abort
  // flag was solving a problem this one-shot restore doesn't have.
  useEffect(() => {
    if (!user || !accessToken) return;
    if (initialPathRef.current === null) return;
    const path = initialPathRef.current;
    initialPathRef.current = null;
    const doneRestoring = () => setRestoringFromUrl(null);
    if (!conversationIdFromUrl && !shareIdFromUrl) {
      const parsed = parseAppPath(path);
      if (parsed.type === 'conversation') {
        handleConversationSelect({ conversation_id: parsed.conversationId }).finally(doneRestoring);
      } else if (parsed.type === 'project') {
        if (parsed.conversationId) {
          handleConversationSelect({ conversation_id: parsed.conversationId }).finally(doneRestoring);
        } else {
          resolveProject(parsed.projectId).then((project) => {
            if (!project) return;
            // Same entry point every other "open a project" action in this
            // file uses -- lands on that project's own landing page (a
            // fresh "start a new conversation" composer), not any specific
            // past conversation.
            handleOpenProjectView(project);
          }).finally(doneRestoring);
        }
      } else {
        doneRestoring();
      }
    } else {
      doneRestoring();
    }
    setUrlRestoreAttempted(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, accessToken]);

  // Mirrors the app's current state into the URL (see computeCanonicalPath)
  // and into LAST_APP_PATH_STORAGE_KEY -- the latter purely as a fallback
  // restore target for AuthCallback.js's MSAL-redirect round trip (a real
  // cross-page navigation that loses the path outright; see that key's own
  // comment). Guarded on urlRestoreAttempted so this doesn't stomp on
  // initialPathRef before the restore effect above has read it. Only
  // touches history when the computed path actually differs from the
  // current one, so this is safe to depend on state that settles over more
  // than one render (e.g. handleConversationSelect's setActiveConversation
  // now, resolveProject/setViewingProject/setView a moment later).
  useEffect(() => {
    if (!urlRestoreAttempted) return;
    // AuthCallback (see currentPath === '/auth/callback' below) has its own
    // pending window.location.href redirect a beat after mount -- a REAL
    // navigation that always wins regardless, but there's no reason for
    // this effect to also rewrite the address bar out from under it while
    // that's in flight.
    if (window.location.pathname === '/auth/callback') return;
    const canonicalPath = computeCanonicalPath({ view, viewingProject, activeConversation, sharedConversation });
    if (canonicalPath === null) return;
    if (window.location.pathname !== canonicalPath) {
      window.history.replaceState({}, '', canonicalPath);
    }
    try {
      sessionStorage.setItem(LAST_APP_PATH_STORAGE_KEY, canonicalPath);
    } catch (e) {
      // Private browsing / storage disabled -- the MSAL-redirect fallback
      // restore just won't have anything to read, which is a reasonable
      // degrade (a plain refresh still works via the URL itself).
    }
  }, [urlRestoreAttempted, view, viewingProject, activeConversation, sharedConversation]);

  // Lands the user in a project the new-project pop-up created when a full-
  // page navigation (the interactive-reauth redirect in utils/api.js, or any
  // other hard reload) interrupted its file uploads -- see
  // PENDING_PROJECT_SETUP_STORAGE_KEY. Same one-shot-ref-then-null shape as
  // the viewing-project restore above, and the same reason it deliberately
  // has no cleanup/cancellation flag (see that effect's comment).
  useEffect(() => {
    if (!user || !accessToken) return;
    const savedProjectId = initialProjectSetupIdRef.current;
    if (!savedProjectId) return;
    initialProjectSetupIdRef.current = null;
    try {
      sessionStorage.removeItem(PENDING_PROJECT_SETUP_STORAGE_KEY);
    } catch (e) {
      // Storage disabled -- nothing to clear.
    }
    resolveProject(savedProjectId).then((project) => {
      if (!project) return;
      handleOpenProjectView(project);
      setMemoryConsolidateToast(
        "Welcome back. Your project was created; check its Knowledge base for any file that didn't finish uploading."
      );
      setTimeout(() => setMemoryConsolidateToast(null), 8000);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, accessToken]);

  // Auto-dismiss the "reconnecting" notice (see AuthContext.js's
  // interactiveReauth) if it's still showing after a few seconds -- the
  // normal case is a full-page redirect firing almost immediately after
  // it's set, which unmounts everything anyway, but this covers the edge
  // case where that redirect never actually happens (e.g. no MSAL account).
  useEffect(() => {
    if (!authNotice) return undefined;
    const timer = setTimeout(() => setAuthNotice(null), 8000);
    return () => clearTimeout(timer);
  }, [authNotice, setAuthNotice]);

  // Load shared conversation when ?share= is present and user is logged in
  useEffect(() => {
    const shareId = shareIdFromUrl;
    if (!shareId || !user || !accessToken) {
      if (!shareId) {
        setSharedConversationError(null);
        setSharedConversation(null);
      }
      return;
    }
    setSharedConversationError(null);
    setSharedConversationLoading(true);
    loadSharedConversation(shareId)
      .then((data) => {
        setSharedConversation({
          shareId,
          conversation: data.conversation,
          messages: data.messages || [],
          ownerName: data.owner_name || '',
          ownerEmail: data.owner_email || '',
          // Set when the conversation is in a project: importing puts the
          // copy there.
          projectName: data.project_name || '',
        });
        setSharedConversationLoading(false);
      })
      .catch((err) => {
        setSharedConversationError(err.message || 'Failed to load shared conversation');
        setSharedConversation(null);
        setSharedConversationLoading(false);
      });
  }, [user, accessToken, shareIdFromUrl]);

  const handleImportShared = async (shareId) => {
    if (isImportingShared) return;
    setIsImportingShared(true);
    try {
      const result = await importSharedConversation(shareId);
      const data = await loadConversation(result.conversation_id);
      // Resolve and apply the imported conversation's own model BEFORE
      // activating it -- same reasoning as the ?conversation= restore effect.
      // This path previously never touched selectedModel at all, so whatever
      // model the user happened to have selected before importing would get
      // PUT over the imported conversation's real model_id.
      const { models, defaultModelId } = await fetchModelConfig();
      const modelId = resolveSelectedModel(
        data.conversation?.model_id,
        models,
        defaultModelId,
      );
      if (modelId !== selectedModel) {
        setSelectedModel(modelId);
      }
      // A conversation from a project is imported into that project, so it
      // opens inside the project's shell like any of its other conversations
      // (see handleConversationSelect).
      const project = await resolveProject(data.conversation?.project_id);
      setActiveConversation(data.conversation);
      setMessages(data.messages || []);
      setInitialContextUsage(data.context_usage || null);
      if (project) {
        setViewingProject(project);
        setView('project');
      } else {
        setView('chat');
      }
      setSharedConversation(null);
      setSharedConversationError(null);
      window.history.replaceState({}, '', window.location.pathname);
      setConversationsRefreshTrigger((prev) => prev + 1);
      setImportedIntoProjectName(project?.name || '');
      setShowImportSuccessModal(true);
    } catch (err) {
      console.error('Import failed:', err);
      alert(err.message || 'Failed to import conversation');
    } finally {
      setIsImportingShared(false);
    }
  };

  // Early returns after all hooks have been called
  // If we're on the auth callback route, show the callback component
  if (currentPath === '/auth/callback') {
    return <AuthCallback />;
  }

  // If authentication is still loading, show loading screen
  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        backgroundColor: '#f5f5f5',
        fontFamily: 'system-ui, -apple-system, sans-serif'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '4px solid #f3f3f3',
            borderTop: '4px solid #0078d4',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 1rem'
          }} />
          <h2 style={{ color: '#333', marginBottom: '0.5rem' }}>Loading...</h2>
          <p style={{ color: '#666', margin: 0 }}>Checking authentication...</p>
          <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      </div>
    );
  }
  
  // If user is not authenticated, show login page
  if (!user || !accessToken) {
    return <LoginPage />;
  }

  const shareId = new URLSearchParams(window.location.search).get('share');
  const showSharedLoading = shareId && sharedConversationLoading;
  const showSharedError = shareId && sharedConversationError && !sharedConversation;

  // Whether any of the mutually-exclusive right-hand drawers is open --
  // whichever the user opens from the header/dropdown takes priority over
  // the project's own Instructions/Knowledge base/History panel (see
  // hidePanel below). The panel is the default; opening any of these is what
  // temporarily displaces it, and closing it brings the panel straight back
  // since this is the only thing gating it.
  const anyRightDrawerOpen = conversationsSidebarOpen || memoryPanelOpen || customRulesPanelOpen || rightSidebarOpen;

  // Is the chat currently showing a conversation that lives in the project
  // being viewed -- either an open one, or one about to be created by the
  // next message? Only then does ChatInterface belong INSIDE ProjectView's
  // shell; otherwise the project shows its landing page and the chat (if
  // any) stays on its own screen.
  const chatBelongsToViewingProject = Boolean(
    viewingProject && !sharedConversation && (
      activeConversation
        ? activeConversation.project_id === viewingProject.project_id
        : pendingProjectForNextConversation?.project_id === viewingProject.project_id
    )
  );
  // A shared conversation always takes the plain chat screen: it isn't the
  // viewer's project (or anyone's, from their side), so the shell's
  // Instructions/Knowledge base/History would describe something the read-only
  // transcript on screen has nothing to do with.
  const showProjectShell = Boolean(
    !showSharedLoading && !showSharedError && !sharedConversation
    && view === 'project' && viewingProject
  );

  // GET /projects now returns both the caller's own projects AND ones
  // shared with them (my_role/shared/unseen tell the two apart) -- one list
  // in the pop-up, own projects first. Tabs split it the way Claude's
  // Projects page does, into yours and the ones shared with you, shown only
  // once there are both. Your own shared projects stay under yours, marked by
  // the line under each name (projectAccessLine) rather than a tab of their own.
  const ownDropdownProjects = dropdownProjects.filter((p) => !p.shared);
  const sharedDropdownProjects = dropdownProjects.filter((p) => p.shared);
  const sharedProjectsUnseenCount = sharedDropdownProjects.filter((p) => p.unseen).length;
  const showProjectTabs = ownDropdownProjects.length > 0 && sharedDropdownProjects.length > 0;
  const projectTabs = [
    { id: 'all', label: 'All', projects: [...ownDropdownProjects, ...sharedDropdownProjects] },
    { id: 'mine', label: 'My projects', projects: ownDropdownProjects },
    { id: 'shared', label: 'Shared with me', projects: sharedDropdownProjects },
  ];
  // Back on All whenever the tabs go away (e.g. the last shared project was left).
  const activeProjectTab = (showProjectTabs && projectTabs.find((t) => t.id === projectsFilter)) || projectTabs[0];
  const projectSearchTerm = projectSearch.trim().toLowerCase();
  const visibleProjects = projectSearchTerm
    ? activeProjectTab.projects.filter((p) => (p.name || '').toLowerCase().includes(projectSearchTerm))
    : activeProjectTab.projects;
  // The Projects icon's badge: unopened shared projects PLUS invitations still
  // waiting for an answer (those aren't in dropdownProjects at all -- an
  // invite grants no access until accepted).
  const projectsBadgeCount = sharedProjectsUnseenCount + projectInvitations.length;

  // Same "who has access" glance ProjectPanel.js's own header avatar stack
  // shows once you're actually inside a project -- rendered here too so it
  // reads before you even open one. Real KPF profile photos (via
  // dropdownProjectMembers, listProjectMembers -> get_kpf_user_info),
  // falling back to an initial exactly like ProjectPanel.js does when a
  // member has no photo. A button: hovering a face names that person,
  // clicking opens the members popup without opening the project -- a
  // sibling of the row's own "open" button, never nested in it.
  const dropdownPeople = (projectId) => {
    const all = dropdownProjectMembers[projectId] || [];
    return { joined: all.filter((m) => !m.pending), pending: all.filter((m) => m.pending) };
  };
  const rowHasPeople = (projectId) => {
    const { joined, pending } = dropdownPeople(projectId);
    return joined.length > 1 || pending.length > 0;
  };

  // The line under each project's name: who your own project is shared with,
  // or who shared someone else's with you and what you can do in it -- the
  // way a file list names each file's owner, in place of a "Shared by me" tab.
  // None for a project you haven't shared: just its name.
  const ACCESS_PHRASES = { edit: 'Can edit', view: 'Can view' };
  const personName = (m) => m.display_name || m.email;
  const projectAccessLine = (project) => {
    const { joined: joinedPeople, pending: pendingPeople } = dropdownPeople(project.project_id);
    if (project.shared) {
      const owner = joinedPeople.find((m) => m.role === 'owner');
      let from = 'Shared with you';
      if (project.ownerless) from = 'Owner has left';
      else if (owner && personName(owner)) from = `Shared by ${personName(owner)}`;
      return `${from} · ${ACCESS_PHRASES[project.my_role] || 'Can view'}`;
    }
    // Counted from the project itself; the member list only adds the name
    // when it's one person, once it has loaded. Someone invited who hasn't
    // accepted yet counts as shared with, with a note saying so.
    const joined = project.shared_with || 0;
    const invited = project.pending_invites || 0;
    const total = joined + invited;
    if (total === 0) return null;
    const others = [...joinedPeople, ...pendingPeople]
      .filter((m) => !(user && m.user_id && m.user_id === user.id));
    const whom = total === 1 && others.length === 1 && personName(others[0])
      ? personName(others[0])
      : `${total} ${total === 1 ? 'person' : 'people'}`;
    if (invited === 0) return `Shared with ${whom}`;
    const notYet = `${invited === 1 ? "hasn't" : "haven't"} accepted yet`;
    return joined === 0
      ? `Shared with ${whom} · ${notYet}`
      : `Shared with ${whom} · ${invited} ${notYet}`;
  };
  const renderAccessLine = (project) => {
    const line = projectAccessLine(project);
    return line ? <span className="projects-dropdown-item-meta">{line}</span> : null;
  };
  const renderDropdownAvatarStack = (project) => {
    const { joined, pending } = dropdownPeople(project.project_id);
    if (joined.length <= 1 && pending.length === 0) return null;
    // Same as the project header's faces (ProjectPanel.js): hovering a face
    // names that person, "+N" names the rest -- " · " between people, since
    // a name itself can hold a comma ("Shaik, Rizwana").
    const nameOf = (m) => `${m.display_name || m.email || 'Unknown member'}${
      user && m.user_id && m.user_id === user.id ? ' (you)' : ''}${m.pending ? ' (invited)' : ''}`;
    // Only people who have joined get an avatar; a project with nothing but
    // unanswered invitations shows their count instead.
    const shown = joined.length > 1 ? joined : [];
    const sep = ' · ';
    const ariaText = `${project.shared ? 'In this project' : 'Shared with'}: ${[...joined, ...pending].map(nameOf).join('; ')}`;
    return (
      <button
        type="button"
        className="projects-dropdown-avatar-stack"
        aria-label={`${ariaText}. Manage members`}
        onClick={(e) => {
          e.stopPropagation();
          setProjectsListOpen(false);
          setMembersModalProject(project);
        }}
      >
        {shown.length === 0 && (
          <span
            className="projects-dropdown-avatar projects-dropdown-avatar-more"
            title={pending.map(nameOf).join(sep)}
          >
            +{pending.length}
          </span>
        )}
        {shown.slice(0, 3).map((m) => {
          const hasPhoto = typeof m.profile_img === 'string' && m.profile_img.trim()
            && m.profile_img.trim().toUpperCase() !== 'N/A';
          return hasPhoto ? (
            <img
              key={m.user_id || m.email}
              src={m.profile_img}
              alt=""
              title={nameOf(m)}
              referrerPolicy="no-referrer"
              className="projects-dropdown-avatar projects-dropdown-avatar-img"
            />
          ) : (
            <span key={m.user_id || m.email} className="projects-dropdown-avatar" title={nameOf(m)}>
              {(m.display_name || m.email || m.user_id || '?').trim().charAt(0).toUpperCase()}
            </span>
          );
        })}
        {shown.length > 3 && (
          <span
            className="projects-dropdown-avatar projects-dropdown-avatar-more"
            title={shown.slice(3).map(nameOf).join(sep)}
          >
            +{shown.length - 3}
          </span>
        )}
      </button>
    );
  };

  // One of the caller's own projects in the Projects pop-up, shared or not.
  const renderOwnDropdownRow = (project) => (
    // A <div>, not a <button>, because it holds its own
    // delete/rename buttons (see .projects-dropdown-item-delete
    // below) -- nesting a button inside a button is
    // invalid HTML and browsers resolve the click
    // unpredictably, the same reason ProjectPanel.js's
    // .project-history-row splits open/delete the same way.
    <div
      key={project.project_id}
      className={`projects-dropdown-item own-row${
        view === 'project' && viewingProject?.project_id === project.project_id ? ' active' : ''
      }`}
    >
      {renamingProjectId === project.project_id ? (
        <div className="projects-dropdown-item-rename-form" onClick={(e) => e.stopPropagation()}>
          <span className="projects-dropdown-item-icon" aria-hidden="true">
            <FiLayers size={16} />
          </span>
          <input
            type="text"
            className="projects-dropdown-item-rename-input"
            value={renameProjectDraft}
            onChange={(e) => setRenameProjectDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleCommitRenameProjectFromDropdown(project);
              if (e.key === 'Escape') handleCancelRenameProjectFromDropdown();
            }}
            autoFocus
            disabled={renamingProjectBusy}
          />
          <button
            type="button"
            className="projects-dropdown-item-rename-save"
            onClick={() => handleCommitRenameProjectFromDropdown(project)}
            disabled={renamingProjectBusy || !renameProjectDraft.trim()}
            title="Save"
            aria-label="Save project name"
          >
            <FiCheck size={13} />
          </button>
          <button
            type="button"
            className="projects-dropdown-item-rename-cancel"
            onClick={handleCancelRenameProjectFromDropdown}
            disabled={renamingProjectBusy}
            title="Cancel"
            aria-label="Cancel rename"
          >
            <FiX size={13} />
          </button>
        </div>
      ) : (
        <>
          <button
            type="button"
            className={`projects-dropdown-item-open${
              rowHasPeople(project.project_id) ? ' has-avatars' : ''
            }`}
            onClick={() => handleOpenProjectView(project)}
            // Hovering is usually a moment before the click: long enough to
            // have the project's Knowledge base list ready when it opens.
            onMouseEnter={() => prefetchProjectFilesOnHover(project.project_id)}
            onMouseLeave={cancelHoverPrefetch}
            onFocus={() => prefetchProjectFiles(project.project_id)}
            title={project.name}
          >
            <span className="projects-dropdown-item-icon" aria-hidden="true">
              <FiLayers size={16} />
            </span>
            <span className="projects-dropdown-item-text">
              <span className="projects-dropdown-item-label">{project.name}</span>
              {renderAccessLine(project)}
            </span>
          </button>
          {renderDropdownAvatarStack(project)}
          <button
            type="button"
            className="projects-dropdown-item-rename"
            onClick={(e) => handleStartRenameProjectFromDropdown(e, project)}
            title={`Rename "${project.name}"`}
            aria-label={`Rename project "${project.name}"`}
          >
            <FiEdit2 size={13} />
          </button>
          <button
            type="button"
            className="projects-dropdown-item-delete"
            onClick={(e) => handleDeleteProjectFromDropdown(e, project)}
            disabled={deletingProjectId === project.project_id}
            title={`Delete "${project.name}"`}
            aria-label={`Delete project "${project.name}"`}
          >
            <FiTrash2 size={13} />
          </button>
        </>
      )}
    </div>
  );

  // A project someone else shared with the caller. No delete action here --
  // leaving a shared project happens from inside it (ProjectMembersModal's own
  // "Leave project"), not a second affordance in this list. See
  // .claude/plan/projectsharing.md.
  const renderSharedDropdownRow = (project) => (
    <div
      key={project.project_id}
      className={`projects-dropdown-item${
        view === 'project' && viewingProject?.project_id === project.project_id ? ' active' : ''
      }`}
    >
      <button
        type="button"
        className={`projects-dropdown-item-open${
          rowHasPeople(project.project_id) ? ' has-avatars' : ''
        }`}
        onClick={() => handleOpenProjectView(project)}
        onMouseEnter={() => prefetchProjectFilesOnHover(project.project_id)}
        onMouseLeave={cancelHoverPrefetch}
        onFocus={() => prefetchProjectFiles(project.project_id)}
        title={project.name}
      >
        <span className="projects-dropdown-item-icon" aria-hidden="true">
          <FiLayers size={16} />
        </span>
        <span className="projects-dropdown-item-text">
          <span className="projects-dropdown-item-label">{project.name}</span>
          {renderAccessLine(project)}
        </span>
        {project.unseen && <span className="projects-dropdown-unseen-dot" aria-label="Unread" />}
      </button>
      {renderDropdownAvatarStack(project)}
    </div>
  );

  // One element, rendered in one of two places: inside the project shell, or
  // as the whole chat column. Built once here so the props can't drift
  // between the two call sites.
  const chatInterface = (
    <ChatInterface
      key={chatSessionKey}
      messages={sharedConversation ? sharedConversation.messages : messages}
      setMessages={setMessages}
      selectedModel={selectedModel}
      setSelectedModel={setSelectedModel}
      selectedFocusMode={selectedFocusMode}
      tokenCount={tokenCount}
      setTokenCount={setTokenCount}
      initialContextUsage={initialContextUsage}
      rightSidebarOpen={rightSidebarOpen}
      toggleRightSidebar={toggleRightSidebar}
      activeConversation={activeConversation}
      setActiveConversation={setActiveConversation}
      onMessageSent={handleMessageSent}
      sharedConversation={sharedConversation}
      onImportShared={handleImportShared}
      websitePreview={websitePreview}
      setWebsitePreview={setWebsitePreview}
      isImportingShared={isImportingShared}
      onConversationContinued={handleConversationContinued}
      onOpenConversation={openConversationById}
      pendingProjectForNextConversation={pendingProjectForNextConversation}
      pendingKnowledgeScope={pendingKnowledgeScope}
      onPendingKnowledgeScopeChange={setPendingKnowledgeScope}
      onProjectConversationFiled={handleProjectConversationFiled}
      onConversationAddedToProject={handleConversationAddedToProject}
      onOpenProjectView={handleOpenProjectView}
      onManageProjectMembers={setMembersModalProject}
      pendingProjectFirstMessage={pendingProjectFirstMessage}
      hideHeaderTitle={showProjectShell && chatBelongsToViewingProject}
      headerActionsPortalTarget={
        showProjectShell && chatBelongsToViewingProject ? projectHeaderActionsEl : null
      }
      headerSubtitlePortalTarget={
        showProjectShell && chatBelongsToViewingProject ? projectHeaderSubtitleEl : null
      }
      onOpenCustomRules={handleDropdownOpenCustomRules}
    />
  );

  return (
    <div className="app">
      {memoryConsolidateToast && (
        <div className="memory-consolidate-toast" role="status">
          {memoryConsolidateToast}
        </div>
      )}
      {errorToast && (
        <div className="memory-consolidate-toast is-error" role="alert">
          {errorToast}
        </div>
      )}
      {authNotice && (
        <div className="memory-consolidate-toast" role="status">
          {authNotice}
        </div>
      )}
      {projectsListOpen && (
        <div
          className="projects-modal-overlay"
          onClick={() => setProjectsListOpen(false)}
          role="presentation"
        >
          <div
            className="projects-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="projects-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            {recycleBinOpen ? (
              <>
                <div className="projects-modal-header">
                  <div className="projects-modal-heading">
                    <button
                      type="button"
                      className="projects-dropdown-back-btn"
                      onClick={() => setRecycleBinOpen(false)}
                      title="Back to Projects"
                      aria-label="Back to Projects"
                    >
                      <FiChevronLeft size={16} />
                    </button>
                    <h2 id="projects-modal-title" className="projects-modal-title">Recycle Bin</h2>
                  </div>
                  <button
                    type="button"
                    className="projects-modal-icon-btn projects-modal-close-btn"
                    onClick={() => setProjectsListOpen(false)}
                    title="Close"
                    aria-label="Close"
                  >
                    <FiX size={18} />
                  </button>
                </div>

                <div className="projects-modal-body">
                  <div className="projects-dropdown-list">
                    {loadingDeletedProjects ? (
                      <div className="projects-dropdown-empty-hint">Loading…</div>
                    ) : deletedProjects.length === 0 ? (
                      <div className="projects-dropdown-empty-hint">Recycle Bin is empty.</div>
                    ) : (
                      deletedProjects.map((project) => (
                        <div key={project.project_id} className="projects-dropdown-item projects-dropdown-item--bin">
                          <span className="projects-dropdown-item-icon" aria-hidden="true">
                            <FiLayers size={16} />
                          </span>
                          <span className="projects-dropdown-bin-item-info">
                            <span className="projects-dropdown-item-label" title={project.name}>{project.name}</span>
                            <span className="projects-dropdown-bin-item-expiry">
                              {project.days_remaining <= 1
                                ? 'Expires today'
                                : `${project.days_remaining} days left`}
                            </span>
                          </span>
                          <button
                            type="button"
                            className="projects-dropdown-item-restore"
                            onClick={() => handleRestoreProject(project)}
                            disabled={restoringProjectId === project.project_id}
                            title={`Restore "${project.name}"`}
                            aria-label={`Restore project "${project.name}"`}
                          >
                            <FiRotateCcw size={13} />
                          </button>
                          <button
                            type="button"
                            className="projects-dropdown-item-delete"
                            onClick={() => setPermanentDeleteConfirm(project)}
                            disabled={permanentlyDeletingProjectId === project.project_id}
                            title={`Delete "${project.name}" forever`}
                            aria-label={`Permanently delete project "${project.name}"`}
                          >
                            <FiTrash2 size={13} />
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="projects-modal-header">
                  <h2 id="projects-modal-title" className="projects-modal-title">
                    Projects
                    {dropdownProjects.length > 0 && (
                      <span className="projects-dropdown-count-badge kai-count-badge">
                        {dropdownProjects.length}
                      </span>
                    )}
                  </h2>
                  <div className="projects-modal-actions">
                    <button
                      type="button"
                      className="projects-modal-icon-btn projects-modal-create-btn"
                      onClick={openNewProjectSetup}
                      title="New project"
                      aria-label="New project"
                    >
                      <FiFolderPlus size={15} />
                    </button>
                    <button
                      type="button"
                      className="projects-modal-icon-btn projects-modal-close-btn"
                      onClick={() => setProjectsListOpen(false)}
                      title="Close"
                      aria-label="Close"
                    >
                      <FiX size={18} />
                    </button>
                  </div>
                </div>

                {/* One card per pending invitation -- "Review" opens the
                    Accept / Decline prompt on it. Stays here until it is
                    accepted or declined, however many times it was put off. */}
                {projectInvitations.map((invitation) => (
                  <InvitationCard
                    key={invitation.project_id}
                    invitation={invitation}
                    onReview={openInvitationPrompt}
                  />
                ))}

                {dropdownProjects.length > 0 && (
                  <div className="projects-modal-search">
                    <FiSearch size={15} className="projects-modal-search-icon" aria-hidden="true" />
                    <input
                      type="text"
                      className="projects-modal-search-input"
                      value={projectSearch}
                      onChange={(e) => setProjectSearch(e.target.value)}
                      placeholder="Search projects"
                      aria-label="Search projects"
                    />
                    {projectSearch && (
                      <button
                        type="button"
                        className="projects-modal-search-clear"
                        onClick={() => setProjectSearch('')}
                        title="Clear search"
                        aria-label="Clear search"
                      >
                        <FiX size={14} />
                      </button>
                    )}
                  </div>
                )}

                {showProjectTabs && (
                  <div className="projects-modal-tabs" role="tablist" aria-label="Show projects">
                    {projectTabs.map((tab) => (
                      <button
                        key={tab.id}
                        type="button"
                        role="tab"
                        aria-selected={activeProjectTab.id === tab.id}
                        className={`projects-modal-tab${activeProjectTab.id === tab.id ? ' active' : ''}`}
                        onClick={() => setProjectsFilter(tab.id)}
                      >
                        {tab.label}
                        {tab.id === 'shared' && sharedProjectsUnseenCount > 0 && (
                          <span className="projects-modal-tab-dot" aria-label="Unread" />
                        )}
                      </button>
                    ))}
                  </div>
                )}

                {/* One size whichever tab is open, and while searching: a
                    hidden copy of the All list (names and the line under
                    them, nothing clickable) sets the list area's height, up
                    to its usual maximum, and the visible list scrolls
                    inside it. Without it the pop-up shrank to fit each tab. */}
                <div className={`projects-modal-body-frame${dropdownProjects.length > 0 ? ' sized' : ''}`}>
                  {dropdownProjects.length > 0 && (
                    <div className="projects-dropdown-list projects-modal-body-sizer" aria-hidden="true">
                      {projectTabs[0].projects.map((project) => (
                        <div key={project.project_id} className="projects-dropdown-item">
                          <div className="projects-dropdown-item-open">
                            <span className="projects-dropdown-item-icon">
                              <FiLayers size={16} />
                            </span>
                            <span className="projects-dropdown-item-text">
                              <span className="projects-dropdown-item-label">{project.name}</span>
                              {renderAccessLine(project)}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="projects-modal-body">
                    <div className="projects-dropdown-list">
                      {dropdownProjects.length === 0 ? (
                        <button
                          type="button"
                          className="projects-dropdown-empty-create"
                          onClick={openNewProjectSetup}
                        >
                          <FiFolderPlus size={14} /> Create your first project
                        </button>
                      ) : visibleProjects.length === 0 ? (
                        <div className="projects-dropdown-empty-hint">
                          No projects match &ldquo;{projectSearch.trim()}&rdquo;.
                        </div>
                      ) : (
                        visibleProjects.map((project) => (
                          project.shared ? renderSharedDropdownRow(project) : renderOwnDropdownRow(project)
                        ))
                      )}
                    </div>
                  </div>
                </div>

                <div className="projects-dropdown-footer">
                  <button
                    type="button"
                    className="projects-dropdown-recycle-bin-link"
                    onClick={openRecycleBin}
                  >
                    {/* Same 36px boxed-icon treatment as a project row's
                        own .projects-dropdown-item-icon above, so this
                        row's icon column lines up with theirs instead of
                        a bare small glyph sitting flush against the text
                        a project name's own icon box doesn't. */}
                    <span className="projects-dropdown-recycle-bin-icon" aria-hidden="true">
                      <FiTrash2 size={15} />
                    </span>
                    Recycle Bin
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      {deleteProjectConfirm && (
        <div
          className="app-modal-overlay"
          onClick={() => !deletingProjectId && setDeleteProjectConfirm(null)}
          role="presentation"
        >
          <div
            className="app-modal"
            role="dialog"
            aria-labelledby="app-delete-project-title"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            {deleteProjectConfirm.shared_with > 0 ? (
              <>
                <h4 id="app-delete-project-title">Delete "{deleteProjectConfirm.name}" for you?</h4>
                <p>
                  {sharedDeleteMessage(deleteProjectConfirm)}
                </p>
              </>
            ) : (
              <>
                <h4 id="app-delete-project-title">Move "{deleteProjectConfirm.name}" to Recycle Bin?</h4>
                <p>{recycleBinMessage(deleteProjectConfirm)}</p>
              </>
            )}
            <div className="app-modal-actions">
              <button
                type="button"
                className="app-modal-cancel"
                onClick={() => setDeleteProjectConfirm(null)}
                disabled={Boolean(deletingProjectId)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="app-modal-confirm"
                onClick={confirmDeleteProjectFromDropdown}
                disabled={Boolean(deletingProjectId)}
              >
                {deleteProjectConfirm.shared_with > 0
                  ? (deletingProjectId ? 'Leaving…' : 'Delete for me')
                  : (deletingProjectId ? 'Moving…' : 'Move to Recycle Bin')}
              </button>
            </div>
          </div>
        </div>
      )}
      {permanentDeleteConfirm && (
        <div
          className="app-modal-overlay"
          onClick={() => !permanentlyDeletingProjectId && setPermanentDeleteConfirm(null)}
          role="presentation"
        >
          <div
            className="app-modal"
            role="dialog"
            aria-labelledby="app-permanent-delete-title"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <h4 id="app-permanent-delete-title">Delete "{permanentDeleteConfirm.name}" forever?</h4>
            <p>
              Its files and instructions will be permanently deleted. This cannot be undone.
              Its conversations stay in your "{projectFolderName(permanentDeleteConfirm)}" folder.
            </p>
            <div className="app-modal-actions">
              <button
                type="button"
                className="app-modal-cancel"
                onClick={() => setPermanentDeleteConfirm(null)}
                disabled={Boolean(permanentlyDeletingProjectId)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="app-modal-confirm"
                onClick={confirmPermanentDelete}
                disabled={Boolean(permanentlyDeletingProjectId)}
              >
                {permanentlyDeletingProjectId ? 'Deleting…' : 'Delete Forever'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Simplified Header */}
      <header className="app-header">
        <div className="header-left">
          
          <div
            className="app-logo"
            onClick={() => {
              // The logo means "go to the plain, regular conversation
              // screen" -- a same-URL reload used to be enough for that
              // (there was nothing else to restore), but now that the URL
              // itself carries the current conversation/project (see
              // computeCanonicalPath above) and a refresh restores from it,
              // reloading in place would just land right back on whatever
              // conversation/project this click is meant to leave. Navigate
              // to "/" outright instead.
              window.location.href = '/';
            }}
            style={{ cursor: 'pointer' }}
          >
            <img src={logoPath} alt="KAI" className="aws-logo" />
            <div className="app-title">
              <span className="title-main">KAI</span>
              
            </div>
          </div>
        </div>
        
        <div className="header-right">
          <button
            className={`sidebar-toggle${view !== 'project' ? ' sidebar-toggle--active' : ''}`}
            onClick={handleConversationsButtonClick}
            title="Conversations"
          >
            <FiMessageSquare size={16} />
          </button>
          {user && (
            <div className="user-dropdown-container">
              <button
                className={`sidebar-toggle${view === 'project' ? ' sidebar-toggle--active' : ''}`}
                onClick={openProjectsList}
                title="Projects"
                aria-label="Projects"
                aria-haspopup="dialog"
              >
                <FiLayers size={16} />
                {/* Unread count across every project shared with this user --
                    see .claude/plan/projectsharing.md. Cleared per-project the
                    moment ProjectView actually opens it (markProjectSeen), not
                    merely by seeing it listed here. */}
                {projectsBadgeCount > 0 && (
                  <span
                    className={`projects-icon-unseen-badge${
                      projectInvitations.length > acknowledgedInvitationCount ? ' projects-icon-unseen-badge--pulse' : ''
                    }`}
                    aria-label={`${projectsBadgeCount} unread shared project(s) or pending invitation(s)`}
                  >
                    {projectsBadgeCount}
                  </span>
                )}
              </button>
            </div>
          )}
          {user && user.email === 'asojka@kpf.com' && (
            <button
              className="sidebar-toggle"
              onClick={toggleRightSidebar}
            >
              {rightSidebarOpen ? <FiChevronLeft size={16} /> : <FiChevronRight size={16} />}
            </button>
          )}
          {user && (
            <div className="user-dropdown-container" ref={userDropdownRef}>
              <button
                className="user-avatar-btn"
                onClick={toggleUserDropdown}
                title="User menu"
                aria-label="User menu"
                aria-expanded={userDropdownOpen}
              >
                {hasValidProfileImg(user.profile_img) ? (
                  <img
                    src={user.profile_img}
                    alt=""
                    className="user-avatar-img"
                    referrerPolicy="no-referrer"
                  />
                ) : (
                  <span className="user-avatar-initials">{getUserInitials(user)}</span>
                )}
              </button>

              {userDropdownOpen && (
                <div className="user-dropdown">
                  <div className="user-dropdown-header">
                    <div className="user-name">{getUserGreeting(user)}</div>
                    <DarkModeToggle className="user-dropdown-theme-toggle" />
                  </div>

                  <div className="user-dropdown-divider" />

                  <div className="user-dropdown-section">
                    <button
                      type="button"
                      className="user-dropdown-item"
                      onClick={handleDropdownOpenMemory}
                    >
                      <span className="focus-mode-thumb placeholder" aria-hidden="true">
                        <FiCpu size={14} />
                      </span>
                      <span className="focus-mode-item-label">Memory</span>
                    </button>
                    <button
                      type="button"
                      className="user-dropdown-item"
                      onClick={handleDropdownOpenCustomRules}
                    >
                      <span className="focus-mode-thumb placeholder" aria-hidden="true">
                        <FiList size={14} />
                      </span>
                      <span className="focus-mode-item-label">My Custom Rules</span>
                    </button>
                  </div>

                  <div className="user-dropdown-divider" />

                  <div className="user-dropdown-section">
                    <div className="user-dropdown-section-label">Focus modes</div>
                    {dropdownFocusModes.map((mode) => {
                      const avatarUrl = hasValidProfileImg(mode.project_avatar)
                        ? mode.project_avatar
                        : null;
                      const shortLabel = (mode.short || '').trim()
                        || (mode.name || mode.id || '?').charAt(0).toUpperCase();
                      return (
                        <button
                          key={mode.id}
                          type="button"
                          className={`user-dropdown-item focus-mode-item${
                            mode.id === selectedFocusMode ? ' active' : ''
                          }`}
                          onClick={() => handleDropdownFocusModeSelect(mode.id)}
                          title={mode.description || mode.name}
                        >
                          <span
                            className={`focus-mode-thumb${avatarUrl ? '' : ' placeholder'}${
                              !avatarUrl && shortLabel.length > 1 ? ' short-label' : ''
                            }`}
                            style={avatarUrl ? { backgroundImage: `url(${avatarUrl})` } : undefined}
                            aria-hidden="true"
                          >
                            {!avatarUrl && shortLabel}
                          </span>
                          <span className="focus-mode-item-label">
                            {mode.name || mode.id}
                          </span>
                        </button>
                      );
                    })}
                  </div>

                  <div className="user-dropdown-divider" />

                  <div className="user-dropdown-section">
                    <div className="user-dropdown-section-label">MCP servers</div>
                    {dropdownMcpServersLoading ? (
                      <div className="user-dropdown-servers-empty">Loading servers…</div>
                    ) : Object.keys(visibleDropdownMcpServers).length === 0 ? (
                      <div className="user-dropdown-servers-empty">No servers for this focus mode</div>
                    ) : (
                      <div className="user-dropdown-servers-list">
                        {Object.entries(visibleDropdownMcpServers).map(([serverName, server]) => {
                          const isChecking = server.online === null || server.online === undefined;
                          const statusClass = isChecking ? 'checking' : (server.online ? 'online' : 'offline');
                          return (
                            <div key={serverName} className={`server-item${server.online === false ? ' server-offline' : ''}`}>
                              <div className="server-info">
                                <div className="server-header">
                                  <span className="server-name">{server.name || serverName}</span>
                                  <span className={`server-status ${statusClass}`}>
                                    {isChecking ? '◌' : '●'}
                                  </span>
                                </div>
                                <div className="server-description">{server.description}</div>
                                {server.online === false && server.connection_error && (
                                  <div className="server-error">{server.connection_error}</div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  <div className="user-dropdown-divider" />

                  <div className="user-dropdown-section">
                    <button
                      type="button"
                      className="user-dropdown-item logout-btn"
                      onClick={handleLogout}
                      title="Sign out"
                    >
                      <span className="focus-mode-thumb placeholder" aria-hidden="true">
                        <FiLogOut size={14} />
                      </span>
                      <span className="focus-mode-item-label">Logout</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="app-main">
        {/* Chat Interface or Shared / Loading / Error */}
        <div className="chat-container">
          {showSharedLoading && (
            <div className="shared-loading">
              <div className="shared-loading-spinner" />
              <p>Loading shared conversation...</p>
            </div>
          )}
          {showSharedError && (
            <div className="shared-error">
              <p>{sharedConversationError}</p>
              <button
                type="button"
                className="shared-error-dismiss"
                onClick={() => {
                  setSharedConversationError(null);
                  window.history.replaceState({}, '', window.location.pathname);
                }}
              >
                Dismiss
              </button>
            </div>
          )}
          {restoringFromUrl && (
            <div
              className={`app-restoring${restoringFromUrl === 'project' ? ' app-restoring--project' : ''}`}
              role="status"
              aria-label="Opening conversation"
            >
              <div className="loading-spinner" />
            </div>
          )}
          {!restoringFromUrl && showProjectShell && (
            <ProjectView
              project={viewingProject}
              onProjectUpdated={(updated) => {
                setDropdownProjects((prev) => prev.map((p) => (p.project_id === updated.project_id ? updated : p)));
                // Only if it's still the project on screen: a save that lands
                // after switching projects (or leaving) must not switch back.
                setViewingProject((prev) => (prev?.project_id === updated.project_id ? updated : prev));
              }}
              onProjectDeleted={handleProjectDeleted}
              onOpenConversation={handleConversationSelect}
              onStartConversationInProject={handleStartConversationInProject}
              activeConversationId={activeConversation?.conversation_id}
              activeConversationName={activeConversation?.name}
              activeConversationKnowledgeScope={activeConversation?.knowledge_scope}
              pendingKnowledgeScope={pendingKnowledgeScope}
              onPendingKnowledgeScopeChange={setPendingKnowledgeScope}
              onConversationDeleted={handleProjectConversationDeleted}
              conversationsRefreshTrigger={conversationsRefreshTrigger}
              conversationActivityTrigger={conversationActivityTrigger}
              hidePanel={anyRightDrawerOpen}
              dimmed={userDropdownOpen}
              onOpenProjectsList={openProjectsList}
              headerActionsSlotRef={setProjectHeaderActionsEl}
              headerSubtitleSlotRef={setProjectHeaderSubtitleEl}
              focusModeName={activeFocusModeName}
            >
              {chatBelongsToViewingProject ? chatInterface : null}
            </ProjectView>
          )}
          {!restoringFromUrl && !showSharedLoading && !showSharedError && !showProjectShell && chatInterface}
        </div>

        {/* Conversations Sidebar */}
        <div 
          className={`sidebar-container conversations ${!conversationsSidebarShown ? 'closed' : ''}${
            userDropdownOpen && conversationsSidebarShown ? ' dimmed-by-dropdown' : ''
          }`}
          style={{ width: conversationsSidebarShown ? `${rightDrawerWidth}px` : '0' }}
        >
          {conversationsSidebarShown && (
            <div
              className="resize-handle right"
              onMouseDown={handleMouseDown('right')}
            />
          )}
          <ConversationsSidebar
            selectedModel={selectedModel}
            activeConversationId={activeConversation?.conversation_id}
            onConversationSelect={handleConversationSelect}
            onNewConversation={handleNewConversation}
            onClose={toggleConversationsSidebar}
            refreshTrigger={conversationsRefreshTrigger + conversationActivityTrigger}
            isOpen={conversationsSidebarShown}
            loadingConversationId={loadingConversationId}
          />
        </div>

        {/* Memory Panel (right side) */}
        <div
          className={`sidebar-container right ${!memoryPanelOpen ? 'closed' : ''}${
            userDropdownOpen && memoryPanelOpen ? ' dimmed-by-dropdown' : ''
          }`}
          style={{ width: memoryPanelOpen ? `${rightDrawerWidth}px` : '0' }}
        >
          {memoryPanelOpen && (
            <div
              className="resize-handle right"
              onMouseDown={handleMouseDown('right')}
            />
          )}
          <MemoryPanel
            isOpen={memoryPanelOpen}
            onClose={toggleMemoryPanel}
            onOpenConversation={(cid) => {
              if (!cid) return;
              setMemoryPanelOpen(false);
              handleConversationSelect({ conversation_id: cid });
            }}
          />
        </div>

        {/* Custom Rules Panel (right side) */}
        <div
          className={`sidebar-container right ${!customRulesPanelOpen ? 'closed' : ''}${
            userDropdownOpen && customRulesPanelOpen ? ' dimmed-by-dropdown' : ''
          }`}
          style={{ width: customRulesPanelOpen ? `${rightDrawerWidth}px` : '0' }}
        >
          {customRulesPanelOpen && (
            <div
              className="resize-handle right"
              onMouseDown={handleMouseDown('right')}
            />
          )}
          <CustomRulesPanel
            isOpen={customRulesPanelOpen}
            onClose={() => setCustomRulesPanelOpen(false)}
          />
        </div>

        {/* Import success modal */}
        {showImportSuccessModal && (
          <div
            className="import-success-overlay"
            onClick={() => setShowImportSuccessModal(false)}
            onKeyDown={(e) => e.key === 'Escape' && setShowImportSuccessModal(false)}
            role="dialog"
            aria-labelledby="import-success-title"
            aria-modal="true"
          >
            <div className="import-success-modal" onClick={(e) => e.stopPropagation()}>
              <div className="import-success-icon">✓</div>
              <h2 id="import-success-title" className="import-success-title">Conversation imported</h2>
              <p className="import-success-text">
                {importedIntoProjectName
                  ? `It's in "${importedIntoProjectName}" now, so you can continue it with the project's files and instructions.`
                  : 'You can continue the conversation in your list.'}
              </p>
              <button
                type="button"
                className="import-success-done"
                onClick={() => setShowImportSuccessModal(false)}
              >
                Done
              </button>
            </div>
          </div>
        )}

        {projectSetupOpen && (
          <ProjectSetupModal
            onClose={() => setProjectSetupOpen(false)}
            onCreate={handleCreateProjectFromSetup}
            onCreated={handleProjectSetupFinished}
          />
        )}

        {/* Accept / decline prompt for project invitations -- renders nothing
            unless open with at least one invitation. Opened from an
            invitation card in the Projects menu, or by an invite email's link. */}
        <ProjectInvitationModal
          invitations={projectInvitations}
          open={invitationPromptOpen}
          focusProjectId={invitationFocusId}
          onClose={closeInvitationPrompt}
          onAnswered={handleInvitationAnswered}
          onAccepted={handleInvitationAccepted}
        />

        {/* Opened from a row's avatars in the Projects pop-up. Closing it
            marks that row's member list for a re-fetch, so the avatars and
            hover text catch up with anyone invited or removed meanwhile. */}
        <ProjectMembersModal
          isOpen={Boolean(membersModalProject)}
          onClose={() => {
            if (membersModalProject) dropdownMembersFetchedRef.current.delete(membersModalProject.project_id);
            setMembersModalProject(null);
          }}
          projectId={membersModalProject?.project_id}
          projectName={membersModalProject?.name}
          createdBy={membersModalProject?.created_by}
          myRole={membersModalProject?.my_role}
          ownerless={Boolean(membersModalProject?.ownerless)}
          onLeaveRequested={() => setLeaveModalProject(membersModalProject)}
        />
        <LeaveProjectModal
          isOpen={Boolean(leaveModalProject)}
          onClose={() => setLeaveModalProject(null)}
          projectId={leaveModalProject?.project_id}
          projectName={leaveModalProject?.name}
          isLastMember={Boolean(leaveModalProject?.ownerless) && (leaveModalProject?.shared_with || 0) <= 1}
          onLeft={() => leaveModalProject && handleProjectDeleted(leaveModalProject.project_id)}
        />

        {/* Right Sidebar */}
        <div
          className={`sidebar-container right ${!rightSidebarOpen ? 'closed' : ''}${
            userDropdownOpen && rightSidebarOpen ? ' dimmed-by-dropdown' : ''
          }`}
          style={{ width: rightSidebarOpen ? `${rightDrawerWidth}px` : '0' }}
        >
          {rightSidebarOpen && (
            <div 
              className="resize-handle right"
              onMouseDown={handleMouseDown('right')}
              ref={rightResizeRef}
            />
          )}
          <RightSidebar
            onClose={toggleRightSidebar}
          />
        </div>
      </main>
    </div>
  );
};

// Main App component wrapped with DarkModeProvider
const AppWithDarkMode = () => {
  return (
    <DarkModeProvider>
      <App />
    </DarkModeProvider>
  );
};

export default AppWithDarkMode;